import React, { useState, useEffect, useCallback } from 'react';
import { Session, Component, Feature, Platform, ComponentStatus, SubDepStatus, TestCase, TestPlan, UserRecord, PlatformItem } from '../../types';
import { apiRequest } from '../../utils/api';
import { formatDate } from '../../utils/date';
import { useToast, Toast } from '../../utils/messages';
import { useProduct } from '../../contexts/ProductContext';
import { useAuth } from '../../contexts/AuthContext';
import { useWorkspace } from '../../contexts/WorkspaceContext';
import TestPlanDetails from '../testplan/TestPlanDetails';
import PDFReportModal, { InsightSection } from './PDFReportModal';
import { generateInsightsPDF } from '../../utils/pdfGenerator';
import PRFeedbackValueTab from './tabs/quality/PRFeedbackValueTab';
import '../../App.css';

interface PlatformTestingStats {
  platform: Platform;
  totalComponents: number;
  testedComponents: number;
  percentage: number;
  sessions: Session[];
}

interface UntestedComponent {
  component: Component;
  platform: Platform;
  feature: Feature;
  lastTested?: string;
}

const Insights: React.FC = () => {
  const { selectedProduct, products } = useProduct();
  const selectedProductId = selectedProduct?.id || '';
  const [sessions, setSessions] = useState<Session[]>([]);
  const [components, setComponents] = useState<Component[]>([]);
  const [features, setFeatures] = useState<Feature[]>([]);
  const [testCases, setTestCases] = useState<TestCase[]>([]);
  const [platforms, setPlatforms] = useState<PlatformItem[]>([]);
  const [sessionTypes, setSessionTypes] = useState<{ id: string; name: string }[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedMilestone, setSelectedMilestone] = useState<string>('all-time');
  const [activeCategory, setActiveCategory] = useState<'overview' | 'coverage' | 'quality' | 'planning' | 'performance'>('overview');
  const [activeTab, setActiveTab] = useState<'platform-coverage' | 'feature-coverage' | 'untested' | 'bug-rates' | 'trends' | 'needs-test-case' | 'platform-map' | 'tester-performance' | 'component-health' | 'test-plan-completion' | 'bug-analysis' | 'bug-discovery-rate' | 'milestone-completion' | 'bug-tracker' | 'pr-feedback-value'>('platform-coverage');
  
  // Helper to set category and default tab
  const handleCategoryChange = (category: typeof activeCategory, defaultTab?: typeof activeTab) => {
    setActiveCategory(category);
    if (defaultTab) {
      setActiveTab(defaultTab);
    }
  };
  const [bugRatesSubTab, setBugRatesSubTab] = useState<'platform' | 'session-type'>('platform');
  const [testPlans, setTestPlans] = useState<TestPlan[]>([]);
  const [users, setUsers] = useState<UserRecord[]>([]);
  const [selectedTestPlan, setSelectedTestPlan] = useState<TestPlan | null>(null);
  const [componentHealthSearch, setComponentHealthSearch] = useState('');
  const [showReportModal, setShowReportModal] = useState(false);
  const [overviewMilestoneFilter, setOverviewMilestoneFilter] = useState<string>('all-time');
  const [milestones, setMilestones] = useState<Record<string, { startDate: string; endDate?: string }>>({});
  const { message, setMessage } = useToast();
  const { user } = useAuth();
  const { currentWorkspace } = useWorkspace();
  const userRole = (currentWorkspace as any)?.role || user?.role || 'user';

  const fetchData = useCallback(async () => {
    if (!selectedProductId) {
      setSessions([]);
      setComponents([]);
      setFeatures([]);
      return;
    }
    
    setLoading(true);
    try {
      const [sessionsData, featuresData] = await Promise.all([
        apiRequest<Session[]>('/api/sessions', {}, { useAuth: true, setMessage }),
        apiRequest<Feature[]>(`/api/features?productId=${selectedProductId}`, {}, { useAuth: true, setMessage })
      ]);
      
      // Filter sessions by productId
      const filteredSessions = Array.isArray(sessionsData) 
        ? sessionsData.filter(s => s.productId === selectedProductId)
        : [];
      
      // Get feature IDs for this product
      const productFeatureIds = new Set(Array.isArray(featuresData) ? featuresData.map(f => f.id) : []);
      
      // Fetch all components and test cases, filter by product features
      const [allComponents, allTestCases] = await Promise.all([
        apiRequest<Component[]>('/api/components', {}, { useAuth: true, setMessage }),
        apiRequest<TestCase[]>('/api/testcases', {}, { useAuth: true, setMessage })
      ]);
      
      const filteredComponents = Array.isArray(allComponents)
        ? allComponents.filter(c => productFeatureIds.has(c.featureId))
        : [];
      
      const filteredTestCases = Array.isArray(allTestCases)
        ? allTestCases.filter(tc => {
            // Include test cases that belong to components in this product, or directly to features in this product
            if (tc.componentId) {
              return filteredComponents.some(c => c.id === tc.componentId);
            }
            if (tc.featureId) {
              return productFeatureIds.has(tc.featureId);
            }
            return false;
          })
        : [];
      
      setSessions(filteredSessions);
      setComponents(filteredComponents);
      setFeatures(Array.isArray(featuresData) ? featuresData : []);
      setTestCases(filteredTestCases);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  }, [selectedProductId, setMessage]);

  const fetchPlatforms = useCallback(async () => {
    try {
      const data = await apiRequest<PlatformItem[]>('/api/platforms', {}, { useAuth: true, setMessage });
      const allPlatforms = Array.isArray(data) ? data : [];
      
      // Filter platforms by selected product
      // Show platforms that either:
      // 1. Have no productIds (applies to all products), OR
      // 2. Include the selected productId in their productIds array
      const filteredPlatforms = selectedProductId
        ? allPlatforms.filter(p => 
            !p.productIds || 
            p.productIds.length === 0 || 
            p.productIds.includes(selectedProductId)
          )
        : allPlatforms; // If no product selected, show all platforms
      
      setPlatforms(filteredPlatforms);
    } catch (error) {
      console.error('Error fetching platforms:', error);
      setPlatforms([]);
    }
  }, [selectedProductId, setMessage]);

  const fetchSessionTypes = useCallback(async () => {
    try {
      const data = await apiRequest<{ id: string; name: string }[]>('/api/sessiontypes', {}, { useAuth: true, setMessage });
      setSessionTypes(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching session types:', error);
      setSessionTypes([]);
    }
  }, [setMessage]);

  const fetchTestPlans = useCallback(async () => {
    if (!selectedProductId) {
      setTestPlans([]);
      return;
    }
    try {
      const data = await apiRequest<TestPlan[]>('/api/testplans', {}, { useAuth: true, setMessage });
      const filtered = Array.isArray(data) 
        ? data.filter(tp => tp.productId === selectedProductId)
        : [];
      setTestPlans(filtered);
    } catch (error) {
      console.error('Error fetching test plans:', error);
      setTestPlans([]);
    }
  }, [selectedProductId, setMessage]);

  const fetchUsers = useCallback(async () => {
    // Only fetch users if user is admin or manager
    if (userRole !== 'admin' && userRole !== 'manager') {
      setUsers([]);
      return;
    }
    try {
      const data = await apiRequest<UserRecord[]>('/api/users', {}, { useAuth: true, setMessage });
      setUsers(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching users:', error);
      setUsers([]);
    }
  }, [userRole, setMessage]);

  const fetchMilestones = useCallback(async () => {
    if (!selectedProductId) {
      setMilestones({});
      return;
    }
    try {
      const apiUrl = `/api/milestones?productId=${encodeURIComponent(selectedProductId)}`;
      const data = await apiRequest<Array<{ name: string; startDate: string; endDate?: string }>>(
        apiUrl,
        {},
        { useAuth: true, setMessage }
      );
      const milestoneMap: Record<string, { startDate: string; endDate?: string }> = {};
      (Array.isArray(data) ? data : []).forEach(m => {
        milestoneMap[m.name] = { startDate: m.startDate, endDate: m.endDate };
      });
      setMilestones(milestoneMap);
    } catch (error) {
      console.error('Error fetching milestones:', error);
      setMilestones({});
    }
  }, [selectedProductId, setMessage]);

  useEffect(() => {
    fetchPlatforms();
    fetchSessionTypes();
    fetchTestPlans();
    fetchUsers();
    fetchMilestones();
  }, [fetchPlatforms, fetchSessionTypes, fetchTestPlans, fetchUsers, fetchMilestones]);

  useEffect(() => {
    if (selectedProductId) {
      fetchData();
      fetchTestPlans();
    } else {
      setSessions([]);
      setComponents([]);
      setFeatures([]);
      setTestPlans([]);
      setLoading(false);
    }
  }, [selectedProductId, fetchData, fetchTestPlans]);

  // Set default overview milestone filter to latest milestone when data loads
  useEffect(() => {
    if (sessions.length > 0 && overviewMilestoneFilter === 'all-time') {
      const milestones = getAvailableMilestones();
      if (milestones.length > 0) {
        setOverviewMilestoneFilter(milestones[0]); // Latest milestone (already sorted descending)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessions, overviewMilestoneFilter]);

  // Refresh data when window regains focus (user returns to tab) - removed to prevent constant reloading
  // useEffect(() => {
  //   const handleFocus = () => {
  //     if (selectedProductId) {
  //       fetchData();
  //     }
  //   };

  //   window.addEventListener('focus', handleFocus);
  //   return () => {
  //     window.removeEventListener('focus', handleFocus);
  //   };
  // }, [selectedProductId, fetchData]);

  // Get unique milestones from sessions
  const getAvailableMilestones = (): string[] => {
    const milestones = new Set<string>();
    sessions.forEach(s => {
      if (s.milestone) {
        milestones.add(s.milestone);
      }
    });
    return Array.from(milestones).sort((a, b) => {
      // Try to sort numerically if possible (e.g., "1.23" vs "1.24")
      const aNum = parseFloat(a);
      const bNum = parseFloat(b);
      if (!isNaN(aNum) && !isNaN(bNum)) {
        return bNum - aNum; // Descending order (newest first)
      }
      return b.localeCompare(a);
    });
  };

  // Helper function to filter components by milestone
  const filterComponentsByMilestone = (componentsToFilter: Component[], selectedMilestone: string): Component[] => {
    if (selectedMilestone === 'all-time') {
      return componentsToFilter;
    }
    
    const milestoneMeta = milestones[selectedMilestone];
    if (!milestoneMeta) {
      // If no metadata, filter by exact milestone match
      return componentsToFilter.filter(c => c.milestone === selectedMilestone);
    }
    
    const milestoneStartDate = new Date(milestoneMeta.startDate);
    
    return componentsToFilter.filter(c => {
      // Include components from the selected milestone
      if (c.milestone === selectedMilestone) {
        return true;
      }
      
      // Include components from milestones with start dates <= selected milestone start date
      const componentMilestoneMeta = milestones[c.milestone];
      if (!componentMilestoneMeta) {
        // If component milestone has no metadata, only include if exact match
        return false;
      }
      
      const componentMilestoneStart = new Date(componentMilestoneMeta.startDate);
      return componentMilestoneStart <= milestoneStartDate;
    });
  };

  // Helper function to filter sessions by milestone
  // Only includes sessions with the exact selected milestone (not earlier milestones)
  const filterSessionsByMilestone = (sessionsToFilter: Session[], selectedMilestone: string): Session[] => {
    if (selectedMilestone === 'all-time') {
      return sessionsToFilter;
    }
    
    // Only include sessions from the exact selected milestone
    return sessionsToFilter.filter(s => s.milestone === selectedMilestone);
  };

  // Get platform testing statistics
  const getPlatformTestingStats = (milestoneFilter?: string): PlatformTestingStats[] => {
    const milestone = milestoneFilter !== undefined ? milestoneFilter : selectedMilestone;
    return platforms.map(p => {
      const platform = p.name as Platform;
      // Filter components by milestone first, then by platform
      const milestoneFilteredComponents = filterComponentsByMilestone(components, milestone);
      // Get all components that have this platform assigned
      const relevantComponents = milestoneFilteredComponents.filter(c => 
        c.platforms && c.platforms.includes(platform)
      );
      
      // Filter sessions by milestone first, then by platform
      const milestoneFilteredSessions = filterSessionsByMilestone(sessions, milestone);
      
      // Get completed sessions for this platform
      const relevantSessions = milestoneFilteredSessions.filter(s => {
        if (s.status !== 'completed') return false;
        if (!((s.platforms && s.platforms.includes(platform)) || s.platform === platform)) return false;
        return true;
      });
      
      // Get unique components that were tested in these sessions on this platform
      const testedComponentIds = new Set<string>();
      relevantSessions.forEach(session => {
        // Check componentStatuses first, fall back to subDepStatuses for legacy
        if (session.componentStatuses && session.componentStatuses.length > 0) {
          session.componentStatuses.forEach((status: ComponentStatus) => {
            if (status.tested) {
              const testedPlatforms = status.testedPlatforms || [];
              // Check if this component was tested on this platform
              if (testedPlatforms.includes(platform) && status.componentId) {
                testedComponentIds.add(status.componentId);
              }
            }
          });
        } else if (session.subDepStatuses && session.subDepStatuses.length > 0) {
          session.subDepStatuses.forEach((status: SubDepStatus) => {
            if (status.tested) {
              const testedPlatforms = status.testedPlatforms || [];
              // Check if this component was tested on this platform
              if (testedPlatforms.includes(platform)) {
                testedComponentIds.add(status.subDepId);
              }
            }
          });
        }
      });
      
      // Only count components that are actually in the relevantComponents list
      const validTestedComponentIds = Array.from(testedComponentIds).filter(id => 
        relevantComponents.some(c => c.id === id)
      );
      
      const percentage = relevantComponents.length > 0
        ? Math.min((validTestedComponentIds.length / relevantComponents.length) * 100, 100)
        : 0;
      
      return {
        platform,
        totalComponents: relevantComponents.length,
        testedComponents: validTestedComponentIds.length,
        percentage: Math.round(percentage * 10) / 10,
        sessions: relevantSessions
      };
    });
  };

  // Get untested components per platform
  const getUntestedComponents = (milestoneFilter?: string): UntestedComponent[] => {
    const milestone = milestoneFilter !== undefined ? milestoneFilter : selectedMilestone;
    const untested: UntestedComponent[] = [];
    
    // Filter components by milestone first
    const milestoneFilteredComponents = filterComponentsByMilestone(components, milestone);
    
    // Filter sessions by milestone first
    const milestoneFilteredSessions = filterSessionsByMilestone(sessions, milestone);
    
    platforms.forEach(p => {
      const platform = p.name as Platform;
      // Get all components that have this platform assigned (from milestone-filtered list)
      const relevantComponents = milestoneFilteredComponents.filter(c => 
        c.platforms && c.platforms.includes(platform)
      );
      
      // Get all completed sessions for this platform (from milestone-filtered list)
      const platformSessions = milestoneFilteredSessions.filter(s => {
        if (s.status !== 'completed') return false;
        if (!((s.platforms && s.platforms.includes(platform)) || s.platform === platform)) return false;
        return true;
      });
      
      relevantComponents.forEach(component => {
        // Check if this component was tested on this platform in any session
        let wasTested = false;
        let lastTested: string | undefined;
        
        for (const session of platformSessions) {
          const sessionComponentIds = (session.components && session.components.length > 0) 
            ? session.components 
            : (session.subDependencies || []);
          
          if (sessionComponentIds.includes(component.id)) {
            // Check componentStatuses first, fall back to subDepStatuses for legacy
            let status: ComponentStatus | SubDepStatus | undefined;
            if (session.componentStatuses && session.componentStatuses.length > 0) {
              status = session.componentStatuses.find(s => s.componentId === component.id);
            } else if (session.subDepStatuses && session.subDepStatuses.length > 0) {
              status = session.subDepStatuses.find(s => s.subDepId === component.id);
            }
            
            if (status?.tested) {
              const testedPlatforms = status.testedPlatforms || [];
              if (testedPlatforms.includes(platform) && session.completedAt) {
              wasTested = true;
              if (!lastTested || new Date(session.completedAt) > new Date(lastTested)) {
                lastTested = session.completedAt;
                }
              }
            }
          }
        }
        
        if (!wasTested) {
          const feature = features.find(f => f.id === component.featureId);
          if (feature) {
            untested.push({
              component,
              platform,
              feature,
              lastTested
            });
          }
        }
      });
    });
    
    return untested.sort((a, b) => {
      if (!a.feature || !b.feature) return 0;
      const featureA = a.feature.name;
      const featureB = b.feature.name;
      if (featureA !== featureB) return featureA.localeCompare(featureB);
      if (!a.component || !b.component) return 0;
      return a.component.name.localeCompare(b.component.name);
    });
  };

  // Helper function to deduplicate bugs by URL
  // Bugs with the same URL found across different platforms count as 1 bug
  const getUniqueBugUrls = (
    bugSources: Array<{ url?: string; componentId?: string; subDepId?: string; featureId?: string }>
  ): Set<string> => {
    const uniqueUrls = new Set<string>();
    bugSources.forEach(bug => {
      if (bug.url && bug.url.trim()) {
        uniqueUrls.add(bug.url.trim());
      }
    });
    return uniqueUrls;
  };

  // Get bug rates per platform
  const getBugRatesPerPlatform = (milestoneFilter?: string) => {
    const milestone = milestoneFilter !== undefined ? milestoneFilter : selectedMilestone;
    // Filter sessions by milestone first
    const milestoneFilteredSessions = filterSessionsByMilestone(sessions, milestone);
    
    // First pass: collect all bug URLs per platform to determine shared bugs
    const allPlatformBugUrls = new Map<Platform, Set<string>>();
    platforms.forEach(p => {
      const platform = p.name as Platform;
      const bugUrls = new Set<string>();
      
      milestoneFilteredSessions.filter(s => {
        if (s.status !== 'completed') return false;
        return (s.platforms && s.platforms.includes(platform)) || s.platform === platform;
      }).forEach(s => {
        (s.bugUrls || []).forEach(bugUrl => {
          if (typeof bugUrl === 'string') return;
          if (bugUrl.platforms && bugUrl.platforms.includes(platform) && bugUrl.url && bugUrl.url.trim()) {
            bugUrls.add(bugUrl.url.trim());
          }
        });
      });
      
      allPlatformBugUrls.set(platform, bugUrls);
    });
    
    return platforms.map(p => {
      const platform = p.name as Platform;
      const platformSessions = milestoneFilteredSessions.filter(s => {
        if (s.status !== 'completed') return false;
        if (!((s.platforms && s.platforms.includes(platform)) || s.platform === platform)) return false;
        return true;
      });
      
      const totalSessions = platformSessions.length;
      const sessionsWithBugs = platformSessions.filter(s => {
        // Check if any components were bugged on this specific platform
        // Check componentStatuses first, fall back to subDepStatuses for legacy
        let buggedOnThisPlatform = false;
        if (s.componentStatuses && s.componentStatuses.length > 0) {
          buggedOnThisPlatform = s.componentStatuses.some(st => {
          if (!st.bugged) return false;
          // If buggedPlatforms is set, check if it includes this platform
          if (st.buggedPlatforms && st.buggedPlatforms.length > 0) {
            return st.buggedPlatforms.includes(platform);
          }
          // Legacy: if buggedPlatforms is not set, check if testedPlatforms includes this platform
          // This handles old data that doesn't have buggedPlatforms
          const testedPlatforms = st.testedPlatforms || [];
          return testedPlatforms.includes(platform);
        });
        } else if (s.subDepStatuses && s.subDepStatuses.length > 0) {
          buggedOnThisPlatform = s.subDepStatuses.some(st => {
            if (!st.bugged) return false;
            // If buggedPlatforms is set, check if it includes this platform
            if (st.buggedPlatforms && st.buggedPlatforms.length > 0) {
              return st.buggedPlatforms.includes(platform);
            }
            // Legacy: if buggedPlatforms is not set, check if testedPlatforms includes this platform
            // This handles old data that doesn't have buggedPlatforms
            const testedPlatforms = st.testedPlatforms || [];
            return testedPlatforms.includes(platform);
          });
        }
        // Check if any bugs are linked to this platform (handle both string[] and BugUrl[] formats)
        const hasPlatformBugs = (s.bugUrls || []).some(bugUrl => {
          if (typeof bugUrl === 'string') return false; // Legacy format - can't determine platform
          return bugUrl.platforms && bugUrl.platforms.includes(platform);
        });
        return buggedOnThisPlatform || hasPlatformBugs;
      }).length;
      
      const bugRate = totalSessions > 0 ? (sessionsWithBugs / totalSessions) * 100 : 0;
      const avgDuration = totalSessions > 0
        ? platformSessions.reduce((sum, s) => sum + (s.duration || 0), 0) / totalSessions
        : 0;
      
      // Get total bugs found on this platform - deduplicated by URL
      const platformBugUrls = allPlatformBugUrls.get(platform) || new Set<string>();
      const totalBugs = platformBugUrls.size;
      
      // Calculate bugs shared with other platforms
      // A bug is "shared" if it appears on this platform AND at least one other platform
      let sharedBugs = 0;
      platformBugUrls.forEach(url => {
        let foundOnOtherPlatform = false;
        platforms.forEach(otherP => {
          const otherPlatform = otherP.name as Platform;
          if (otherPlatform !== platform) {
            const otherBugUrls = allPlatformBugUrls.get(otherPlatform);
            if (otherBugUrls && otherBugUrls.has(url)) {
              foundOnOtherPlatform = true;
            }
          }
        });
        if (foundOnOtherPlatform) {
          sharedBugs++;
        }
      });
      
      return {
        platform,
        totalSessions,
        sessionsWithBugs,
        bugRate: Math.round(bugRate * 10) / 10,
        avgDuration: Math.round(avgDuration / 60), // Convert to minutes
        totalBugs,
        sharedBugs
      };
    });
  };

  // Get bug rates per feature - using the same correlation logic as FeatureDetail
  // FeatureDetail uses: s.features && s.features.includes(feature.id)
  const getBugRatesPerFeature = (milestoneFilter?: string) => {
    const milestone = milestoneFilter !== undefined ? milestoneFilter : selectedMilestone;
    // Filter components by milestone first
    const milestoneFilteredComponents = filterComponentsByMilestone(components, milestone);
    // Filter sessions by milestone first
    const milestoneFilteredSessions = filterSessionsByMilestone(sessions, milestone);
    
    return features.map(feature => {
      // Get all components for this feature (for bug counting) - from milestone-filtered components
      const featureComponentIds = milestoneFilteredComponents
        .filter(c => c.featureId === feature.id)
        .map(c => c.id);
      
      // Use the same correlation logic as FeatureDetail page
      // Check if session.features includes this feature (same as /api/sessions/by-feature/:featureId)
      const featureSessions = milestoneFilteredSessions.filter(s => {
        // Must be completed
        if (s.status !== 'completed') return false;
        
        // Must have this feature in the features array (same logic as FeatureDetail)
        if (!s.features || !s.features.includes(feature.id)) return false;
        
        return true;
      });
      
      const totalSessions = featureSessions.length;
      const sessionsWithBugs = featureSessions.filter(s => {
        // Check if any components from this feature were bugged
        // Check componentStatuses first, fall back to subDepStatuses for legacy
        // Only count bugs for components in the milestone-filtered list
        let buggedComponents = 0;
        if (s.componentStatuses && s.componentStatuses.length > 0) {
          buggedComponents = s.componentStatuses.filter(st => {
            if (!st.bugged) return false;
            // Only count if component is in milestone-filtered list
            if (!st.componentId || !featureComponentIds.includes(st.componentId)) return false;
            const component = milestoneFilteredComponents.find(c => c.id === st.componentId);
            return component !== undefined;
          }).length;
        } else if (s.subDepStatuses && s.subDepStatuses.length > 0) {
          buggedComponents = s.subDepStatuses.filter(st => {
            if (!st.bugged) return false;
            // Only count if component is in milestone-filtered list
            if (!featureComponentIds.includes(st.subDepId)) return false;
            const component = milestoneFilteredComponents.find(c => c.id === st.subDepId);
            return component !== undefined;
          }).length;
        }
        // Count bugs linked to this feature (handle both string[] and BugUrl[] formats)
        const additionalBugs = (s.bugUrls || []).filter(bugUrl => {
          if (typeof bugUrl === 'string') return false; // Legacy format - can't determine feature
          return bugUrl.featureId === feature.id;
        }).length;
        return buggedComponents > 0 || additionalBugs > 0;
      }).length;
      
      const bugRate = totalSessions > 0 ? (sessionsWithBugs / totalSessions) * 100 : 0;
      const avgDuration = totalSessions > 0
        ? featureSessions.reduce((sum, s) => sum + (s.duration || 0), 0) / totalSessions
        : 0;
      
      // Get total bugs found in this feature - deduplicated by URL
      // Bugs with the same URL across different platforms/sessions count as 1 bug
      const allBugUrlsForFeature: Array<{ url?: string; componentId?: string; subDepId?: string; featureId?: string }> = [];
      
      featureSessions.forEach(s => {
        // Collect bugs from bugUrls linked to this feature
        (s.bugUrls || []).forEach(bugUrl => {
          if (typeof bugUrl === 'string') return; // Legacy format - skip
          if (bugUrl.featureId === feature.id) {
            allBugUrlsForFeature.push(bugUrl);
          }
        });
      });
      
      // Get unique bug count by URL (deduplicates across platforms)
      const uniqueBugUrls = getUniqueBugUrls(allBugUrlsForFeature);
      const totalBugs = uniqueBugUrls.size;
      
      return {
        feature,
        totalSessions,
        sessionsWithBugs,
        bugRate: Math.round(bugRate * 10) / 10,
        avgDuration: Math.round(avgDuration / 60), // Convert to minutes
        totalBugs
      };
    }).filter(stat => stat !== null && stat.totalSessions > 0) // Only show features with sessions
      .sort((a, b) => b!.bugRate - a!.bugRate) as Array<{
        feature: Feature;
        totalSessions: number;
        sessionsWithBugs: number;
        bugRate: number;
        avgDuration: number;
        totalBugs: number;
      }>; // Sort by bug rate descending
  };

  // Get bug rates per session type
  const getBugRatesPerSessionType = (milestoneFilter?: string) => {
    const milestone = milestoneFilter !== undefined ? milestoneFilter : selectedMilestone;
    
    // Filter sessions by milestone first
    const milestoneFilteredSessions = filterSessionsByMilestone(sessions, milestone);
    
    return sessionTypes.map(st => {
      const sessionType = st.name;
      const sessionTypeSessions = milestoneFilteredSessions.filter(s => {
        if (s.status !== 'completed') return false;
        if (s.sessionType !== sessionType) return false;
        return true;
      });
      
      const totalSessions = sessionTypeSessions.length;
      const sessionsWithBugs = sessionTypeSessions.filter(s => {
        // Check if any components were bugged
        // Check componentStatuses first, fall back to subDepStatuses for legacy
        let hasBugs = false;
        if (s.componentStatuses && s.componentStatuses.length > 0) {
          hasBugs = s.componentStatuses.some(st => st.bugged);
        } else if (s.subDepStatuses && s.subDepStatuses.length > 0) {
          hasBugs = s.subDepStatuses.some(st => st.bugged);
        }
        // Check if any bugs are linked (handle both string[] and BugUrl[] formats)
        const hasLinkedBugs = (s.bugUrls || []).length > 0;
        return hasBugs || hasLinkedBugs;
      }).length;
      
      const bugRate = totalSessions > 0 ? (sessionsWithBugs / totalSessions) * 100 : 0;
      const avgDuration = totalSessions > 0
        ? sessionTypeSessions.reduce((sum, s) => sum + (s.duration || 0), 0) / totalSessions
        : 0;
      
      // Get total bugs found - deduplicated by URL (same bug URL across platforms counts as 1)
      const uniqueBugUrls = new Set<string>();
      sessionTypeSessions.forEach(s => {
        (s.bugUrls || []).forEach(bugUrl => {
          if (typeof bugUrl === 'string') return;
          if (bugUrl.url && bugUrl.url.trim()) {
            uniqueBugUrls.add(bugUrl.url.trim());
          }
        });
      });
      
      const totalBugs = uniqueBugUrls.size;
      
      return {
        sessionType,
        totalSessions,
        sessionsWithBugs,
        bugRate: Math.round(bugRate * 10) / 10,
        avgDuration: Math.round(avgDuration / 60), // Convert to minutes
        totalBugs
      };
    }).filter(stat => stat.totalSessions > 0) // Only show session types with sessions
      .sort((a, b) => b.bugRate - a.bugRate); // Sort by bug rate descending
  };

  // Get testing trends
  const getTestingTrends = (milestoneFilter?: string) => {
    const milestone = milestoneFilter !== undefined ? milestoneFilter : selectedMilestone;
    const trends: { date: string; platform: Platform; count: number }[] = [];
    
    // Filter sessions by milestone first
    const milestoneFilteredSessions = filterSessionsByMilestone(sessions, milestone);
    
    // Group sessions by date and platform
    const sessionsByDate: Record<string, Record<Platform, number>> = {};
    
    milestoneFilteredSessions
      .filter(s => {
        if (s.status !== 'completed') return false;
        if (!s.completedAt) return false;
        return true;
      })
      .forEach(session => {
        const date = formatDate(session.completedAt!, 'yyyy-MM-dd');
        if (!sessionsByDate[date]) {
          sessionsByDate[date] = { Android: 0, Windows: 0, XR: 0, Linux: 0, Mac: 0 };
        }
        // Count session for each platform it was tested on
        const sessionPlatforms = session.platforms || (session.platform ? [session.platform] : []);
        sessionPlatforms.forEach(platform => {
          sessionsByDate[date][platform] = (sessionsByDate[date][platform] || 0) + 1;
        });
      });
    
    Object.entries(sessionsByDate).forEach(([date, platformCounts]) => {
      Object.entries(platformCounts).forEach(([platform, count]) => {
        if (count > 0) {
          trends.push({ date, platform: platform as Platform, count });
        }
      });
    });
    
    return trends.sort((a, b) => a.date.localeCompare(b.date));
  };

  // Get components that have test cases marked as needing test case creation
  const getComponentsNeedingTestCases = (milestoneFilter?: string): Array<{ component: Component; feature: Feature; testCases: TestCase[] }> => {
    const milestone = milestoneFilter !== undefined ? milestoneFilter : selectedMilestone;
    const result: Array<{ component: Component; feature: Feature; testCases: TestCase[] }> = [];
    
    // Filter components by milestone first
    const milestoneFilteredComponents = filterComponentsByMilestone(components, milestone);
    
    // Group test cases by component
    // A test case "needs" creation if needsTestCase is true AND it doesn't have a qaseId yet
    const testCasesByComponent = new Map<string, TestCase[]>();
    testCases.forEach(tc => {
      if (tc.componentId && tc.needsTestCase && !tc.qaseId) {
        if (!testCasesByComponent.has(tc.componentId)) {
          testCasesByComponent.set(tc.componentId, []);
        }
        testCasesByComponent.get(tc.componentId)!.push(tc);
      }
    });
    
    // For each component with test cases needing creation, add it to the result
    // Only include components that are in the milestone-filtered list
    testCasesByComponent.forEach((componentTestCases, componentId) => {
      const component = milestoneFilteredComponents.find(c => c.id === componentId);
      if (component) {
        const feature = features.find(f => f.id === component.featureId);
        if (feature) {
          result.push({
            component,
            feature,
            testCases: componentTestCases
          });
        }
      }
    });
    
    // Sort by feature name, then component name
    return result.sort((a, b) => {
      if (!a.feature || !b.feature) return 0;
      const featureCompare = a.feature.name.localeCompare(b.feature.name);
      if (featureCompare !== 0) return featureCompare;
      if (!a.component || !b.component) return 0;
      return a.component.name.localeCompare(b.component.name);
    });
  };

  // Get platform testing map data - time spent per platform per session type
  const getPlatformTestingMap = (milestoneFilter?: string) => {
    const milestone = milestoneFilter !== undefined ? milestoneFilter : selectedMilestone;
    
    // Filter sessions by milestone first
    const milestoneFilteredSessions = filterSessionsByMilestone(sessions, milestone);
    
    // Get completed sessions with duration
    const relevantSessions = milestoneFilteredSessions.filter(s => {
      if (s.status !== 'completed') return false;
      if (!s.duration) return false;
      return true;
    });

    // Calculate time spent per platform per session type
    const mapData: Record<string, Record<string, {
      totalDuration: number; // in seconds
      sessionCount: number;
      subDepCount: number;
    }>> = {};

    // Initialize all platform-session type combinations
    platforms.forEach(p => {
      const platform = p.name;
      mapData[platform] = {};
      sessionTypes.forEach(st => {
        mapData[platform][st.name] = {
          totalDuration: 0,
          sessionCount: 0,
          subDepCount: 0
        };
      });
    });

    // Aggregate data from sessions
    relevantSessions.forEach(session => {
      const sessionPlatforms = session.platforms || (session.platform ? [session.platform] : []);
      const duration = session.duration || 0;
      const componentCount = (session.components && session.components.length > 0) 
        ? session.components.length 
        : (session.subDependencies?.length || 0);
      
      // Distribute duration across platforms (if multiple platforms, divide evenly)
      const durationPerPlatform = duration / sessionPlatforms.length;
      
      sessionPlatforms.forEach(platform => {
        if (mapData[platform] && mapData[platform][session.sessionType]) {
          mapData[platform][session.sessionType].totalDuration += durationPerPlatform;
          mapData[platform][session.sessionType].sessionCount += 1;
          mapData[platform][session.sessionType].subDepCount += componentCount; // Keep field name for compatibility
        }
      });
    });

    return mapData;
  };

  // ========== NEW ANALYTICS FUNCTIONS ==========

  // Tester Performance & Productivity (Admin/Manager Only)
  interface TesterPerformance {
    userId: string;
    displayName: string;
    sessionsCompleted: number;
    sessionsCreated: number;
    completionRate: number;
    avgDuration: number; // in minutes
    componentsTested: number;
    bugsFound: number;
    activeSessions: number;
    primaryPlatform: Platform | null;
  }

  const getTesterPerformance = (milestoneFilter?: string): TesterPerformance[] => {
    const milestone = milestoneFilter !== undefined ? milestoneFilter : selectedMilestone;
    const testerMap = new Map<string, TesterPerformance>();
    const platformCounts = new Map<string, Map<Platform, number>>(); // userId -> platform -> count

    // Initialize all users
    users.forEach(u => {
      const displayName = u.firstName && u.lastName 
        ? `${u.firstName} ${u.lastName}`
        : u.firstName || u.lastName || u.email.split('@')[0];
      testerMap.set(u.id, {
        userId: u.id,
        displayName,
        sessionsCompleted: 0,
        sessionsCreated: 0,
        completionRate: 0,
        avgDuration: 0,
        componentsTested: 0,
        bugsFound: 0,
        activeSessions: 0,
        primaryPlatform: null
      });
      platformCounts.set(u.id, new Map());
    });

    // Process sessions
    sessions.forEach(session => {
      // Filter by milestone
      if (milestone !== 'all-time' && session.milestone !== milestone) {
        return;
      }

      // session.user is a string (user ID or display name)
      const userId = session.user;
      // Try to find user by ID first, then by display name
      let matchedUser = users.find(u => u.id === userId);
      if (!matchedUser) {
        // Try matching by display name
        matchedUser = users.find(u => {
          const displayName = u.firstName && u.lastName 
            ? `${u.firstName} ${u.lastName}`
            : u.firstName || u.lastName || u.email.split('@')[0];
          return displayName === userId;
        });
      }
      if (!matchedUser || !testerMap.has(matchedUser.id)) return;
      const actualUserId = matchedUser.id;

      const tester = testerMap.get(actualUserId)!;
      tester.sessionsCreated++;

      // Track platform usage
      const sessionPlatforms = session.platforms || (session.platform ? [session.platform] : []);
      sessionPlatforms.forEach((platform: Platform) => {
        const userPlatformCounts = platformCounts.get(actualUserId)!;
        userPlatformCounts.set(platform, (userPlatformCounts.get(platform) || 0) + 1);
      });

      if (session.status === 'completed') {
        tester.sessionsCompleted++;
        
        // Calculate duration
        if (session.duration) {
          tester.avgDuration += session.duration;
        }

        // Calculate time to completion
        // Count components tested
        if (session.componentStatuses && session.componentStatuses.length > 0) {
          tester.componentsTested += session.componentStatuses.filter(cs => cs.tested).length;
        } else if (session.subDepStatuses && session.subDepStatuses.length > 0) {
          tester.componentsTested += session.subDepStatuses.filter(sds => sds.tested).length;
        }

        // Count bugs found - count unique bug URLs only
        if (session.bugUrls && session.bugUrls.length > 0) {
          const uniqueUrls = new Set<string>();
          session.bugUrls.forEach(bu => {
            if (typeof bu === 'string') return;
            if (bu.url && bu.url.trim()) {
              uniqueUrls.add(bu.url.trim());
            }
          });
          tester.bugsFound += uniqueUrls.size;
        }
      } else if (session.status === 'inprogress' || session.status === 'todo') {
        tester.activeSessions++;
      }
    });

    // Calculate averages, completion rates, and primary platform
    const results = Array.from(testerMap.values()).map(tester => {
      tester.completionRate = tester.sessionsCreated > 0 
        ? (tester.sessionsCompleted / tester.sessionsCreated) * 100 
        : 0;
      tester.avgDuration = tester.sessionsCompleted > 0 
        ? tester.avgDuration / tester.sessionsCompleted / 60 // Convert to minutes
        : 0;
      
      // Find primary platform
      const userPlatformCounts = platformCounts.get(tester.userId);
      if (userPlatformCounts && userPlatformCounts.size > 0) {
        let maxCount = 0;
        let primaryPlatform: Platform | null = null;
        userPlatformCounts.forEach((count, platform) => {
          if (count > maxCount) {
            maxCount = count;
            primaryPlatform = platform;
          }
        });
        tester.primaryPlatform = primaryPlatform;
      }
      
      return tester;
    });

    return results.filter(t => t.sessionsCreated > 0).sort((a, b) => b.sessionsCompleted - a.sessionsCompleted);
  };

  // Component Health Analytics
  interface ComponentHealth {
    component: Component;
    feature: Feature;
    healthScore: number | null; // 0-100, or null if never tested
    testFrequency: number; // Number of times tested
    bugRate: number; // Percentage of tests that found bugs
    lastTested: string | undefined;
    daysSinceLastTest: number;
    bugRecurrenceRate: number; // Percentage of tests where bugs recurred
    trend: 'increasing' | 'decreasing' | 'stable'; // Bug rate trend
  }

  const getComponentHealth = (milestoneFilter?: string): ComponentHealth[] => {
    const milestone = milestoneFilter !== undefined ? milestoneFilter : selectedMilestone;
    // Filter components by milestone first
    const milestoneFilteredComponents = filterComponentsByMilestone(components, milestone);
    
    // Filter sessions by milestone first
    const milestoneFilteredSessions = filterSessionsByMilestone(sessions, milestone);
    
    return milestoneFilteredComponents.map(component => {
      const feature = features.find(f => f.id === component.featureId);
      if (!feature) return null;

      // Get all sessions that tested this component (from milestone-filtered sessions)
      const componentSessions = milestoneFilteredSessions.filter(s => {
        if (s.status !== 'completed') return false;
        
        const componentIds = s.components || s.subDependencies || [];
        return componentIds.includes(component.id);
      });

      // Get component statuses for this component
      const componentTestHistory: Array<{ tested: boolean; bugged: boolean; date: string; bugCount: number }> = [];
      let lastTested: string | undefined;
      let totalTests = 0;
      let testsWithBugs = 0;
      let bugRecurrences = 0;
      let previousHadBugs = false;
      const testedPlatforms = new Set<Platform>(); // Track which platforms have tested this component
      const platformTestCounts = new Map<Platform, number>(); // Track test count per platform
      
      // Collect all bug URLs for this component across all sessions for deduplication
      const allBugUrlsForComponent: Array<{ url?: string; componentId?: string; subDepId?: string; featureId?: string }> = [];

      componentSessions.forEach(session => {
        let status: ComponentStatus | SubDepStatus | undefined;
        if (session.componentStatuses && session.componentStatuses.length > 0) {
          status = session.componentStatuses.find(cs => cs.componentId === component.id);
        } else if (session.subDepStatuses && session.subDepStatuses.length > 0) {
          status = session.subDepStatuses.find(sds => sds.subDepId === component.id);
        }

        if (status && status.tested && session.completedAt) {
          totalTests++;
          
          // Collect bugs from bugUrls linked to this component
          let sessionHasBug = false;
          if (session.bugUrls && session.bugUrls.length > 0) {
            session.bugUrls.forEach(bu => {
              if (typeof bu === 'string') return;
              if (bu.componentId === component.id || bu.subDepId === component.id) {
                allBugUrlsForComponent.push(bu);
                sessionHasBug = true;
              }
            });
          }
          
          componentTestHistory.push({
            tested: true,
            bugged: sessionHasBug,
            date: session.completedAt,
            bugCount: sessionHasBug ? 1 : 0 // Will be recalculated after deduplication
          });

          if (!lastTested || new Date(session.completedAt) > new Date(lastTested)) {
            lastTested = session.completedAt;
          }

          // Track tested platforms and count tests per platform
          if (status.testedPlatforms && status.testedPlatforms.length > 0) {
            status.testedPlatforms.forEach(platform => {
              testedPlatforms.add(platform);
              platformTestCounts.set(platform, (platformTestCounts.get(platform) || 0) + 1);
            });
          }
          // If no testedPlatforms specified but component was tested, try to infer from session
          else if (session.platforms && session.platforms.length > 0) {
            session.platforms.forEach(platform => {
              testedPlatforms.add(platform);
              platformTestCounts.set(platform, (platformTestCounts.get(platform) || 0) + 1);
            });
          } else if (session.platform) {
            testedPlatforms.add(session.platform);
            platformTestCounts.set(session.platform, (platformTestCounts.get(session.platform) || 0) + 1);
          }

          if (sessionHasBug) {
            testsWithBugs++;
            if (previousHadBugs) {
              bugRecurrences++;
            }
            previousHadBugs = true;
          } else {
            previousHadBugs = false;
          }
        }
      });
      
      // Get unique bug count by URL (deduplicates across platforms/sessions)
      const uniqueBugUrls = getUniqueBugUrls(allBugUrlsForComponent);
      const totalBugsFound = uniqueBugUrls.size;

      // Calculate metrics
      const testFrequency = totalTests;
      const bugRate = totalTests > 0 ? (testsWithBugs / totalTests) * 100 : 0;
      const bugRecurrenceRate = totalTests > 0 ? (bugRecurrences / totalTests) * 100 : 0;
      const daysSinceLastTest = lastTested 
        ? Math.floor((new Date().getTime() - new Date(lastTested).getTime()) / (1000 * 60 * 60 * 24))
        : 999;

      // Calculate trend (compare first half vs second half of test history)
      let trend: 'increasing' | 'decreasing' | 'stable' = 'stable';
      if (componentTestHistory.length >= 4) {
        const midpoint = Math.floor(componentTestHistory.length / 2);
        const firstHalf = componentTestHistory.slice(0, midpoint);
        const secondHalf = componentTestHistory.slice(midpoint);
        const firstHalfBugRate = firstHalf.filter(t => t.bugged).length / firstHalf.length;
        const secondHalfBugRate = secondHalf.filter(t => t.bugged).length / secondHalf.length;
        
        if (secondHalfBugRate > firstHalfBugRate + 0.1) {
          trend = 'increasing';
        } else if (secondHalfBugRate < firstHalfBugRate - 0.1) {
          trend = 'decreasing';
        }
      }

      // Calculate health score (0-100) - only if component has been tested at least 1 time on each assigned platform
      // Factors: test frequency (0-33.33), platform coverage (0-33.33), bug rate inverse (0-33.33)
      let healthScore: number | null = null;
      const assignedPlatforms = component.platforms || [];
      
      // Check if component meets minimum test requirement: at least 1 test on each assigned platform
      let meetsMinimumRequirement = true;
      if (assignedPlatforms.length > 0) {
        // Check if each assigned platform has at least 1 test
        meetsMinimumRequirement = assignedPlatforms.every(platform => {
          const testCount = platformTestCounts.get(platform) || 0;
          return testCount >= 1;
        });
      } else {
        // If no platforms assigned, require at least 1 total test
        meetsMinimumRequirement = totalTests >= 1;
      }
      
      if (totalTests > 0 && meetsMinimumRequirement) {
        // Dynamic weighting based on importance:
        // Bug Rate Inverse: 40 points (most important - quality indicator)
        // Test Frequency: 30 points (important for confidence)
        // Platform Coverage: 30 points (important for completeness)
        
        // Test Frequency: Full 30 points if over 5 tests
        const frequencyScore = testFrequency > 5 ? 30 : (testFrequency / 5) * 30;
        
        // Platform Coverage: Full 30 points if tested on all assigned platforms
        const testedAssignedPlatforms = Array.from(testedPlatforms).filter(p => assignedPlatforms.includes(p));
        const platformCoverage = assignedPlatforms.length > 0 
          ? testedAssignedPlatforms.length / assignedPlatforms.length 
          : 1; // Fallback to 1 if no platforms (shouldn't happen in production)
        const platformCoverageScore = platformCoverage * 30; // 0-30 points based on coverage percentage
        
        // Bug Rate Inverse: More gradual penalty based on bugs vs tests ratio
        // Compare total bugs found against total tests (not just bug rate percentage)
        // This makes a single bug in many tests less impactful
        const bugsToTestsRatio = totalBugsFound / totalTests; // e.g., 1 bug in 10 tests = 0.1, 5 bugs in 10 tests = 0.5
        
        // Gradual penalty: 40 points for 0 bugs, scales down as ratio increases
        // Formula: 40 * (1 - ratio^1.5) - this makes it more gradual than quadratic
        // Examples:
        // - 0 bugs in 10 tests (ratio 0.0) = 40 points
        // - 1 bug in 10 tests (ratio 0.1) = ~36.4 points
        // - 2 bugs in 10 tests (ratio 0.2) = ~31.7 points
        // - 5 bugs in 10 tests (ratio 0.5) = ~14.1 points
        // - 10 bugs in 10 tests (ratio 1.0) = 0 points
        let bugRateScore = 40 * (1 - Math.pow(Math.min(bugsToTestsRatio, 1), 1.5));
        
        // Extra penalty if trend is increasing (bugs getting worse)
        if (trend === 'increasing') {
          bugRateScore = Math.max(0, bugRateScore - 5); // Additional 5 point penalty for increasing trend
        }
        
        healthScore = Math.round(frequencyScore + platformCoverageScore + bugRateScore);
      }

      return {
        component,
        feature,
        healthScore,
        testFrequency,
        bugRate: Math.round(bugRate * 10) / 10,
        lastTested,
        daysSinceLastTest,
        bugRecurrenceRate: Math.round(bugRecurrenceRate * 10) / 10,
        trend
      };
    }).filter((ch): ch is ComponentHealth => ch !== null && ch !== undefined);
  };

  // Test Plan Completion Analytics
  interface TestPlanCompletion {
    testPlan: TestPlan;
    completionRate: number; // Percentage of platforms completed
    platformStatus: Array<{
      platform: Platform;
      status: 'tested-per-plan' | 'tested-in-milestone' | 'not-tested';
      componentsTested: number;
      componentsTotal: number;
      testedComponents: Component[];
    }>;
  }

  const getTestPlanCompletion = (milestoneFilter?: string): TestPlanCompletion[] => {
    const filterMilestone = milestoneFilter !== undefined ? milestoneFilter : selectedMilestone;
    
    // Filter test plans by milestone if a milestone is selected
    let filteredTestPlans = testPlans;
    if (filterMilestone && filterMilestone !== 'all-time') {
      filteredTestPlans = testPlans.filter(tp => {
        // Include test plans that match the selected milestone
        if (tp.milestone === filterMilestone) {
          return true;
        }
        // Also include test plans with no milestone if filtering by all-time (shouldn't happen here, but safety check)
        if (filterMilestone === 'all-time') {
          return true;
        }
        return false;
      });
    }
    
    // Filter sessions by milestone (including sessions from earlier milestones)
    const filteredSessions = filterSessionsByMilestone(sessions, filterMilestone);
    
    // Filter components by milestone
    const milestoneFilteredComponents = filterComponentsByMilestone(components, filterMilestone);
    
    return filteredTestPlans.map(testPlan => {
      if (!testPlan.components || testPlan.components.length === 0) {
        return {
          testPlan,
          completionRate: 0,
          platformStatus: []
        };
      }

      const requiredPlatforms = testPlan.testingPlan || [];
      const milestone = testPlan.milestone || '';
      const testPlanUrl = `${window.location.origin}/testplan/${testPlan.id}`;

      // Group by platform instead of component
      const platformStatus = requiredPlatforms.map(platform => {
        // Find all components that need to be tested on this platform
        // Only include components that are in the milestone-filtered list
        const componentsToTest = testPlan.components!.map(componentId => {
          const component = milestoneFilteredComponents.find(c => c.id === componentId);
          return component;
        }).filter((c): c is Component => c !== null);

        // Find sessions linked to this test plan for this platform
        const linkedSessions = filteredSessions.filter(s => 
          s.status === 'completed' && 
          s.testPlanUrl === testPlanUrl &&
          ((s.platforms && s.platforms.includes(platform)) || s.platform === platform) &&
          testPlan.components!.some(compId => 
            s.components?.includes(compId) || s.subDependencies?.includes(compId)
          )
        );

        // Find sessions in milestone (not linked) for this platform
        const milestoneSessions = milestone ? filteredSessions.filter(s =>
          s.status === 'completed' &&
          s.milestone === milestone &&
          ((s.platforms && s.platforms.includes(platform)) || s.platform === platform) &&
          testPlan.components!.some(compId => 
            s.components?.includes(compId) || s.subDependencies?.includes(compId)
          ) &&
          s.testPlanUrl !== testPlanUrl
        ) : [];

        // Check which components were tested
        const testedComponentIds = new Set<string>();
        let testedPerPlan = false;
        let testedInMilestone = false;

        // Check linked sessions (tested per plan)
        linkedSessions.forEach(session => {
          testPlan.components!.forEach(componentId => {
            let status: ComponentStatus | SubDepStatus | undefined;
            if (session.componentStatuses && session.componentStatuses.length > 0) {
              status = session.componentStatuses.find(cs => cs.componentId === componentId);
            } else if (session.subDepStatuses && session.subDepStatuses.length > 0) {
              status = session.subDepStatuses.find(sds => sds.subDepId === componentId);
            }

            if (status && status.tested && status.testedPlatforms?.includes(platform)) {
              testedComponentIds.add(componentId);
              testedPerPlan = true;
            }
          });
        });

        // Check milestone sessions (tested in milestone)
        if (!testedPerPlan) {
          milestoneSessions.forEach(session => {
            testPlan.components!.forEach(componentId => {
              let status: ComponentStatus | SubDepStatus | undefined;
              if (session.componentStatuses && session.componentStatuses.length > 0) {
                status = session.componentStatuses.find(cs => cs.componentId === componentId);
              } else if (session.subDepStatuses && session.subDepStatuses.length > 0) {
                status = session.subDepStatuses.find(sds => sds.subDepId === componentId);
              }

              if (status && status.tested && status.testedPlatforms?.includes(platform)) {
                testedComponentIds.add(componentId);
                testedInMilestone = true;
              }
            });
          });
        }

        const testedComponents = Array.from(testedComponentIds)
          .map(id => milestoneFilteredComponents.find(c => c.id === id))
          .filter((c): c is Component => c !== null && c !== undefined);

        let status: 'tested-per-plan' | 'tested-in-milestone' | 'not-tested';
        if (testedPerPlan && testedComponentIds.size === componentsToTest.length) {
          status = 'tested-per-plan';
        } else if (testedInMilestone && testedComponentIds.size === componentsToTest.length) {
          status = 'tested-in-milestone';
        } else if (testedComponentIds.size > 0) {
          // Partially tested - still show as tested-in-milestone if any were tested
          status = testedPerPlan ? 'tested-per-plan' : 'tested-in-milestone';
        } else {
          status = 'not-tested';
        }

        return {
          platform,
          status,
          componentsTested: testedComponentIds.size,
          componentsTotal: componentsToTest.length,
          testedComponents
        };
      });

      const completedPlatforms = platformStatus.filter(ps => ps.status !== 'not-tested').length;
      const completionRate = platformStatus.length > 0 
        ? (completedPlatforms / platformStatus.length) * 100 
        : 0;

      return {
        testPlan,
        completionRate: Math.round(completionRate * 10) / 10,
        platformStatus
      };
    });
  };

  // General Analytics
  interface FeatureCoverage {
    feature: Feature;
    coverage: number; // Percentage of components tested
    totalComponents: number;
    testedComponents: number;
  }

  interface MilestoneCompletion {
    milestone: string;
    testPlanCompletion: number;
    sessionCompletion: number;
    overallCompletion: number;
  }

  interface BugTrend {
    date: string;
    bugs: number;
    buggedComponents: number;
  }

  const getFeatureCoverage = (milestoneFilter?: string): FeatureCoverage[] => {
    const milestone = milestoneFilter !== undefined ? milestoneFilter : selectedMilestone;
    // Filter components by milestone first
    const milestoneFilteredComponents = filterComponentsByMilestone(components, milestone);
    
    // Filter sessions by milestone first
    const milestoneFilteredSessions = filterSessionsByMilestone(sessions, milestone);
    
    return features.map(feature => {
      const featureComponents = milestoneFilteredComponents.filter(c => c.featureId === feature.id);
      
      // Collect all unique platforms assigned to components in this feature
      const featurePlatforms = new Set<Platform>();
      featureComponents.forEach(component => {
        if (component.platforms && component.platforms.length > 0) {
          component.platforms.forEach((platform: Platform) => {
            featurePlatforms.add(platform);
          });
        }
      });

      // If no platforms are assigned to any components, fall back to component-based coverage
      if (featurePlatforms.size === 0) {
        const featureComponentIds = new Set(featureComponents.map(c => c.id));
        const testedComponentIds = new Set<string>();

        milestoneFilteredSessions.filter(s => {
          if (s.status !== 'completed') return false;
          return true;
        }).forEach(session => {
          if (session.componentStatuses && session.componentStatuses.length > 0) {
            session.componentStatuses.forEach(cs => {
              if (cs.tested && cs.componentId && featureComponentIds.has(cs.componentId)) {
                testedComponentIds.add(cs.componentId);
              }
            });
          } else if (session.subDepStatuses && session.subDepStatuses.length > 0) {
            session.subDepStatuses.forEach(sds => {
              if (sds.tested && sds.subDepId && featureComponentIds.has(sds.subDepId)) {
                testedComponentIds.add(sds.subDepId);
              }
            });
          }
        });

        const coverage = featureComponents.length > 0
          ? (testedComponentIds.size / featureComponents.length) * 100
          : 0;

        return {
          feature,
          coverage: Math.round(coverage * 10) / 10,
          totalComponents: featureComponents.length,
          testedComponents: testedComponentIds.size
        };
      }

      // Platform-based coverage: track which component-platform combinations have been tested
      const componentPlatformCombinations = new Map<string, Set<Platform>>();
      featureComponents.forEach(component => {
        if (component.platforms && component.platforms.length > 0) {
          componentPlatformCombinations.set(component.id, new Set(component.platforms));
        }
      });

      const testedComponentPlatforms = new Map<string, Set<Platform>>();

      milestoneFilteredSessions.filter(s => {
        if (s.status !== 'completed') return false;
        return true;
      }).forEach(session => {
        if (session.componentStatuses && session.componentStatuses.length > 0) {
          session.componentStatuses.forEach(cs => {
            if (cs.tested && cs.componentId && cs.testedPlatforms && cs.testedPlatforms.length > 0) {
              const component = featureComponents.find(c => c.id === cs.componentId);
              if (component) {
                if (!testedComponentPlatforms.has(cs.componentId)) {
                  testedComponentPlatforms.set(cs.componentId, new Set<Platform>());
                }
                cs.testedPlatforms.forEach((platform: Platform) => {
                  // Only count platforms that are assigned to this component
                  if (component.platforms && component.platforms.includes(platform)) {
                    testedComponentPlatforms.get(cs.componentId)!.add(platform);
                  }
                });
              }
            }
          });
        } else if (session.subDepStatuses && session.subDepStatuses.length > 0) {
          session.subDepStatuses.forEach(sds => {
            if (sds.tested && sds.subDepId && sds.testedPlatforms && sds.testedPlatforms.length > 0) {
              const component = featureComponents.find(c => c.id === sds.subDepId);
              if (component) {
                if (!testedComponentPlatforms.has(sds.subDepId)) {
                  testedComponentPlatforms.set(sds.subDepId, new Set<Platform>());
                }
                sds.testedPlatforms.forEach((platform: Platform) => {
                  if (component.platforms && component.platforms.includes(platform)) {
                    testedComponentPlatforms.get(sds.subDepId)!.add(platform);
                  }
                });
              }
            }
          });
        }
      });

      // Calculate coverage: (tested platform-component combinations) / (total platform-component combinations)
      let totalCombinations = 0;
      let testedCombinations = 0;

      componentPlatformCombinations.forEach((platforms, componentId) => {
        totalCombinations += platforms.size;
        const testedPlatforms = testedComponentPlatforms.get(componentId) || new Set<Platform>();
        testedCombinations += testedPlatforms.size;
      });

      const coverage = totalCombinations > 0
        ? (testedCombinations / totalCombinations) * 100
        : 0;

      return {
        feature,
        coverage: Math.round(coverage * 10) / 10,
        totalComponents: featureComponents.length,
        testedComponents: testedComponentPlatforms.size
      };
    });
  };

  const getMilestoneCompletion = (): MilestoneCompletion[] => {
    const milestoneNames = new Set<string>();
    sessions.forEach(s => {
      if (s.milestone) milestoneNames.add(s.milestone);
    });
    testPlans.forEach(tp => {
      if (tp.milestone) milestoneNames.add(tp.milestone);
    });

    return Array.from(milestoneNames).map(milestoneName => {
      const milestoneTestPlans = testPlans.filter(tp => tp.milestone === milestoneName);
      const milestoneSessions = sessions.filter(s => s.milestone === milestoneName);
      
      // Count sessions by status
      const completedSessions = milestoneSessions.filter(s => s.status === 'completed').length;
      const totalSessions = milestoneSessions.length;

      // Calculate test plan completion
      const testPlanCompletion = milestoneTestPlans.length > 0
        ? milestoneTestPlans.filter(tp => tp.status === 'completed').length / milestoneTestPlans.length * 100
        : 0;

      // Calculate session completion: completed / (completed + in-progress + todo)
      // This accounts for open sessions - only completed sessions count as "done"
      const sessionCompletion = totalSessions > 0
        ? (completedSessions / totalSessions) * 100
        : 0;

      // Overall completion: average only if both exist, otherwise use the one that exists
      const hasTestPlans = milestoneTestPlans.length > 0;
      const hasSessions = totalSessions > 0;
      let overallCompletion = 0;
      
      if (hasTestPlans && hasSessions) {
        // Both exist: average them
        overallCompletion = (testPlanCompletion + sessionCompletion) / 2;
      } else if (hasTestPlans) {
        // Only test plans exist: use test plan completion
        overallCompletion = testPlanCompletion;
      } else if (hasSessions) {
        // Only sessions exist: use session completion
        overallCompletion = sessionCompletion;
      }
      // If neither exists, overallCompletion remains 0

      return {
        milestone: milestoneName,
        testPlanCompletion: Math.round(testPlanCompletion * 10) / 10,
        sessionCompletion: Math.round(sessionCompletion * 10) / 10,
        overallCompletion: Math.round(overallCompletion * 10) / 10
      };
    }).sort((a, b) => {
      // Sort by milestone start date if available, otherwise by name
      const aMeta = milestones[a.milestone];
      const bMeta = milestones[b.milestone];
      if (aMeta?.startDate && bMeta?.startDate) {
        return new Date(bMeta.startDate).getTime() - new Date(aMeta.startDate).getTime();
      }
      return b.milestone.localeCompare(a.milestone);
    });
  };

  const getBugDiscoveryRate = (milestoneFilter?: string): BugTrend[] => {
    const milestone = milestoneFilter !== undefined ? milestoneFilter : selectedMilestone;
    
    // First, collect all bugs with their earliest discovery date
    const bugUrlFirstSeen: Map<string, string> = new Map(); // url -> first date
    const bugComponentPairFirstSeen: Map<string, string> = new Map(); // url::componentId -> first date

    // Get all completed sessions sorted by date (earliest first)
    const completedSessions = sessions
      .filter(s => {
      if (s.status !== 'completed') return false;
      if (!s.completedAt) return false;
      if (milestone !== 'all-time' && s.milestone !== milestone) return false;
      return true;
      })
      .sort((a, b) => (a.completedAt || '').localeCompare(b.completedAt || ''));

    // Track first seen date for each bug
    completedSessions.forEach(session => {
      const date = formatDate(session.completedAt!, 'yyyy-MM-dd');

      if (session.bugUrls && session.bugUrls.length > 0) {
        session.bugUrls.forEach(bu => {
          if (typeof bu === 'string') return;
          const url = bu.url?.trim();
          if (url) {
            // Only record first occurrence
            if (!bugUrlFirstSeen.has(url)) {
              bugUrlFirstSeen.set(url, date);
            }
            // Track unique URL+component pairs
            const componentId = bu.componentId || bu.subDepId;
            if (componentId) {
              const pairKey = `${url}::${componentId}`;
              if (!bugComponentPairFirstSeen.has(pairKey)) {
                bugComponentPairFirstSeen.set(pairKey, date);
              }
            }
          }
        });
      }
    });

    // Now count bugs per date (only counting first discovery)
    const bugUrlsByDate: Record<string, number> = {};
    const bugComponentPairsByDate: Record<string, number> = {};

    bugUrlFirstSeen.forEach((date) => {
      bugUrlsByDate[date] = (bugUrlsByDate[date] || 0) + 1;
    });

    bugComponentPairFirstSeen.forEach((date) => {
      bugComponentPairsByDate[date] = (bugComponentPairsByDate[date] || 0) + 1;
    });

    // Get all dates that have any bugs
    const allDates = new Set([...Object.keys(bugUrlsByDate), ...Object.keys(bugComponentPairsByDate)]);

    return Array.from(allDates)
      .map(date => ({ 
        date, 
        bugs: bugUrlsByDate[date] || 0,
        buggedComponents: bugComponentPairsByDate[date] || 0
      }))
      .sort((a, b) => a.date.localeCompare(b.date));
  };

  const getMostBugProneComponents = (milestoneFilter?: string): Array<{ component: Component; feature: Feature; bugRate: number; totalTests: number; bugsFound: number }> => {
    const milestone = milestoneFilter !== undefined ? milestoneFilter : selectedMilestone;
    // Filter components by milestone first
    const milestoneFilteredComponents = filterComponentsByMilestone(components, milestone);
    // Filter sessions by milestone first
    const milestoneFilteredSessions = filterSessionsByMilestone(sessions, milestone);
    
    // Track test counts per component
    const componentTestCounts = new Map<string, number>();
    // Track bug URLs per component for deduplication
    const componentBugUrls = new Map<string, Set<string>>();

    const completedSessions = milestoneFilteredSessions.filter(s => s.status === 'completed');
    
    completedSessions.forEach(session => {
      if (session.componentStatuses && session.componentStatuses.length > 0) {
        session.componentStatuses.forEach(cs => {
          if (cs.tested && cs.componentId) {
            componentTestCounts.set(cs.componentId, (componentTestCounts.get(cs.componentId) || 0) + 1);
          }
        });
      } else if (session.subDepStatuses && session.subDepStatuses.length > 0) {
        session.subDepStatuses.forEach(sds => {
          if (sds.tested) {
            componentTestCounts.set(sds.subDepId, (componentTestCounts.get(sds.subDepId) || 0) + 1);
          }
        });
      }
      // Collect bug URLs per component for deduplication
      if (session.bugUrls && session.bugUrls.length > 0) {
        session.bugUrls.forEach(bu => {
          if (typeof bu === 'string') return;
          const compId = bu.componentId || bu.subDepId;
          if (compId && bu.url && bu.url.trim()) {
            if (!componentBugUrls.has(compId)) {
              componentBugUrls.set(compId, new Set());
            }
            componentBugUrls.get(compId)!.add(bu.url.trim());
          }
        });
      }
    });

    return Array.from(componentTestCounts.entries())
      .map(([componentId, totalTests]) => {
        // Only include components from milestone-filtered list
        const component = milestoneFilteredComponents.find(c => c.id === componentId);
        if (!component) return null;
        const feature = features.find(f => f.id === component.featureId);
        if (!feature) return null;
        
        // Get unique bug count by URL (deduplicates across platforms)
        const bugsFound = componentBugUrls.get(componentId)?.size || 0;
        const bugRate = totalTests > 0 ? (bugsFound / totalTests) * 100 : 0;
        
        // Only include if bug rate > 20% (bugs found in more than 1 in 5 tests)
        if (bugRate <= 20) return null;
        
        return { component, feature, bugRate, totalTests, bugsFound };
      })
      .filter((item): item is NonNullable<typeof item> => item !== null)
      .sort((a, b) => b.bugRate - a.bugRate);
  };

  const getMostBugProneFeatures = (milestoneFilter?: string): Array<{ feature: Feature; uniqueBugUrls: number; buggedComponents: number }> => {
    const milestone = milestoneFilter !== undefined ? milestoneFilter : selectedMilestone;
    // Filter sessions by milestone first
    const milestoneFilteredSessions = filterSessionsByMilestone(sessions, milestone);
    
    // Track unique bug URLs per feature (no dupes at all)
    const featureBugUrls = new Map<string, Set<string>>();
    // Track unique (URL + component) pairs per feature (deduped across platforms)
    const featureBugComponentPairs = new Map<string, Set<string>>();

    const completedSessions = milestoneFilteredSessions.filter(s => s.status === 'completed');
    
    completedSessions.forEach(session => {
      if (session.bugUrls && session.bugUrls.length > 0) {
        session.bugUrls.forEach(bu => {
          if (typeof bu === 'string') return;
          const url = bu.url?.trim();
          if (bu.featureId && url) {
            // Track unique bug URLs
            if (!featureBugUrls.has(bu.featureId)) {
              featureBugUrls.set(bu.featureId, new Set());
            }
            featureBugUrls.get(bu.featureId)!.add(url);
            
            // Track unique (URL + component) pairs
            const componentId = bu.componentId || bu.subDepId;
            if (componentId) {
              if (!featureBugComponentPairs.has(bu.featureId)) {
                featureBugComponentPairs.set(bu.featureId, new Set());
              }
              featureBugComponentPairs.get(bu.featureId)!.add(`${url}::${componentId}`);
            }
          }
        });
      }
    });

    // Get all feature IDs that have bugs
    const allFeatureIds = new Set([...Array.from(featureBugUrls.keys()), ...Array.from(featureBugComponentPairs.keys())]);

    return Array.from(allFeatureIds)
      .map(featureId => {
        const feature = features.find(f => f.id === featureId);
        if (!feature) return null;
        return { 
          feature, 
          uniqueBugUrls: featureBugUrls.get(featureId)?.size || 0,
          buggedComponents: featureBugComponentPairs.get(featureId)?.size || 0
        };
      })
      .filter((item): item is NonNullable<typeof item> => item !== null)
      .sort((a, b) => b.uniqueBugUrls - a.uniqueBugUrls)
      .slice(0, 10);
  };

  // Bug Tracker - detailed bug data by feature and component
  interface BugWithPlatforms {
    url: string;
    platforms: Platform[];
  }

  interface BugTrackerComponent {
    component: Component;
    bugs: BugWithPlatforms[];
  }

  interface BugTrackerFeature {
    feature: Feature;
    uniqueBugUrls: number;
    buggedComponentsCount: number;
    components: BugTrackerComponent[];
  }

  const getBugTrackerData = (milestoneFilter?: string): BugTrackerFeature[] => {
    const milestone = milestoneFilter !== undefined ? milestoneFilter : selectedMilestone;
    const milestoneFilteredSessions = filterSessionsByMilestone(sessions, milestone);
    const completedSessions = milestoneFilteredSessions.filter(s => s.status === 'completed');

    // Track bugs per feature and component with platforms
    // featureId -> componentId -> url -> Set of platforms
    const featureComponentBugPlatforms = new Map<string, Map<string, Map<string, Set<Platform>>>>();
    // Track all unique bug URLs per feature
    const featureBugUrls = new Map<string, Set<string>>();

    completedSessions.forEach(session => {
      if (session.bugUrls && session.bugUrls.length > 0) {
        session.bugUrls.forEach(bu => {
          if (typeof bu === 'string') return;
          const url = bu.url?.trim();
          const featureId = bu.featureId;
          const componentId = bu.componentId || bu.subDepId;
          const bugPlatforms = bu.platforms || [];

          if (url && featureId) {
            // Track unique URLs per feature
            if (!featureBugUrls.has(featureId)) {
              featureBugUrls.set(featureId, new Set());
            }
            featureBugUrls.get(featureId)!.add(url);

            // Track bugs per component within feature with platforms
            if (componentId) {
              if (!featureComponentBugPlatforms.has(featureId)) {
                featureComponentBugPlatforms.set(featureId, new Map());
              }
              const componentMap = featureComponentBugPlatforms.get(featureId)!;
              if (!componentMap.has(componentId)) {
                componentMap.set(componentId, new Map());
              }
              const urlMap = componentMap.get(componentId)!;
              if (!urlMap.has(url)) {
                urlMap.set(url, new Set());
              }
              bugPlatforms.forEach(p => urlMap.get(url)!.add(p));
            }
          }
        });
      }
    });

    // Build the result
    const result: BugTrackerFeature[] = [];

    featureBugUrls.forEach((bugUrlSet, featureId) => {
      const feature = features.find(f => f.id === featureId);
      if (!feature) return;

      const componentMap = featureComponentBugPlatforms.get(featureId) || new Map<string, Map<string, Set<Platform>>>();
      const trackerComponents: BugTrackerComponent[] = [];

      componentMap.forEach((urlMap: Map<string, Set<Platform>>, componentId: string) => {
        const component = components.find(c => c.id === componentId);
        if (component) {
          const bugs: BugWithPlatforms[] = [];
          urlMap.forEach((platformSet: Set<Platform>, url: string) => {
            bugs.push({
              url,
              platforms: Array.from(platformSet).sort()
            });
          });
          // Sort bugs by URL
          bugs.sort((a, b) => a.url.localeCompare(b.url));
          trackerComponents.push({
            component,
            bugs
          });
        }
      });

      // Sort components by bug count descending
      trackerComponents.sort((a, b) => b.bugs.length - a.bugs.length);

      result.push({
        feature,
        uniqueBugUrls: bugUrlSet.size,
        buggedComponentsCount: trackerComponents.length,
        components: trackerComponents
      });
    });

    // Sort features by unique bug URLs descending
    result.sort((a, b) => b.uniqueBugUrls - a.uniqueBugUrls);

    return result;
  };

  // Calculate computed analytics
  const testerPerformance = getTesterPerformance();
  const componentHealth = getComponentHealth();
  const testPlanCompletion = getTestPlanCompletion(selectedMilestone);
  const featureCoverage = getFeatureCoverage();
  const milestoneCompletion = getMilestoneCompletion();
  const allBugDiscoveryRate = getBugDiscoveryRate();
  const mostBugProneComponents = getMostBugProneComponents();
  const mostBugProneFeatures = getMostBugProneFeatures();

  // Bug discovery rate (already filtered by milestone)
  const bugDiscoveryRate = allBugDiscoveryRate;

  const platformStats = getPlatformTestingStats();
  const untestedComponents = getUntestedComponents();
  const bugRatesPerPlatform = getBugRatesPerPlatform();
  const bugRatesPerFeature = getBugRatesPerFeature();
  const bugRatesPerSessionType = getBugRatesPerSessionType();
  const trends = getTestingTrends();
  const componentsNeedingTestCases = getComponentsNeedingTestCases();

  if (loading && !selectedProductId) {
    return <div className="form-container">Loading...</div>;
  }

  if (products.length === 0) {
    return (
      <div className="form-container">
        <Toast message={message} />
        <div className="page-header">
          <h1 className="page-title">Insights</h1>
        </div>
        <div className="card insights-empty-state">
          <div className="insights-empty-icon">📊</div>
          <h3 className="insights-empty-title">No products found</h3>
          <p className="insights-empty-text">Please create a product in Settings → Manage Projects to view insights.</p>
        </div>
      </div>
    );
  }

  if (!selectedProductId) {
    return (
      <div className="form-container">
        <Toast message={message} />
        <div className="page-header">
          <h1 className="page-title">Insights</h1>
        </div>
        <div className="card insights-empty-state">
          <div className="insights-empty-icon">📊</div>
          <h3 className="insights-empty-title">No product selected</h3>
          <p className="insights-empty-text">Please select a product from the navigation bar to view insights.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="form-container">
        <Toast message={message} />
        <div className="page-header">
          <h1 className="page-title">Insights</h1>
        </div>
        <div className="form-container">Loading insights...</div>
      </div>
    );
  }

  return (
    <div className="form-container">
      <Toast message={message} />
      <div className="page-header">
        <h1 className="page-title">Insights</h1>
        <div className="insights-header-controls">
          <div className="insights-control-group">
            <span className="insights-control-label">Milestone:</span>
            <select
              value={selectedMilestone}
              onChange={(e) => setSelectedMilestone(e.target.value)}
              className="insights-select"
            >
              <option value="all-time">All Time</option>
              {getAvailableMilestones().map(milestone => {
                const milestoneMeta = milestones[milestone];
                const dateRange = milestoneMeta 
                  ? (milestoneMeta.endDate 
                      ? `${formatDate(milestoneMeta.startDate, 'MMM dd, yyyy')} - ${formatDate(milestoneMeta.endDate, 'MMM dd, yyyy')}`
                      : `Started: ${formatDate(milestoneMeta.startDate, 'MMM dd, yyyy')}`)
                  : '';
                return (
                  <option key={milestone} value={milestone}>
                    {milestone}{dateRange ? ` (${dateRange})` : ''}
                  </option>
                );
              })}
            </select>
          </div>
          <button
            className="btn btn-primary"
            onClick={() => setShowReportModal(true)}
          >
            📄 Generate PDF Report
          </button>
        </div>
      </div>

      <div className="insights-layout">
        {/* Sidebar Navigation */}
        <div className="insights-sidebar">
          <div className="insights-sidebar-header">
            <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: '700', color: 'var(--text-primary)' }}>Categories</h3>
          </div>
          <nav className="insights-sidebar-nav">
            <button
              className={`insights-sidebar-item ${activeCategory === 'overview' ? 'active' : ''}`}
              onClick={() => handleCategoryChange('overview', 'platform-coverage')}
            >
              <span className="insights-sidebar-icon">📊</span>
              <span>Overview</span>
            </button>
            <button
              className={`insights-sidebar-item ${activeCategory === 'coverage' ? 'active' : ''}`}
              onClick={() => handleCategoryChange('coverage', 'platform-coverage')}
            >
              <span className="insights-sidebar-icon">✅</span>
              <span>Coverage</span>
            </button>
            <button
              className={`insights-sidebar-item ${activeCategory === 'quality' ? 'active' : ''}`}
              onClick={() => handleCategoryChange('quality', 'bug-rates')}
            >
              <span className="insights-sidebar-icon">🐛</span>
              <span>Quality</span>
            </button>
            <button
              className={`insights-sidebar-item ${activeCategory === 'planning' ? 'active' : ''}`}
              onClick={() => handleCategoryChange('planning', 'test-plan-completion')}
            >
              <span className="insights-sidebar-icon">📋</span>
              <span>Planning</span>
            </button>
            {(userRole === 'admin' || userRole === 'manager') && (
              <button
                className={`insights-sidebar-item ${activeCategory === 'performance' ? 'active' : ''}`}
                onClick={() => handleCategoryChange('performance', 'tester-performance')}
              >
                <span className="insights-sidebar-icon">⚡</span>
                <span>Performance</span>
              </button>
            )}
          </nav>
        </div>

        {/* Main Content Area */}
        <div className="insights-content">
          {/* Category-specific tabs */}
          {activeCategory === 'overview' && (
            <div className="insights-overview-dashboard">
              <h2 className="insights-section-title">Insights Overview</h2>
              <p className="insights-section-subtitle">
                Key metrics at a glance{selectedMilestone === 'all-time' ? ' (all time)' : ` for milestone ${selectedMilestone}`}.
              </p>
              
              <div className="insights-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', marginBottom: '2rem' }}>
                <div className="card insights-card">
                  <div className="insights-stat-label">Total Sessions</div>
                  <div className="insights-stat-value" style={{ fontSize: '2.5rem' }}>
                    {sessions.filter(s => {
                      if (selectedMilestone !== 'all-time' && s.milestone !== selectedMilestone) return false;
                      return true;
                    }).length}
                  </div>
                </div>
                <div className="card insights-card">
                  <div className="insights-stat-label">Completed Sessions</div>
                  <div className="insights-stat-value" style={{ fontSize: '2.5rem' }}>
                    {sessions.filter(s => {
                      if (s.status !== 'completed') return false;
                      if (selectedMilestone !== 'all-time' && s.milestone !== selectedMilestone) return false;
                      return true;
                    }).length}
                  </div>
                </div>
                <div className="card insights-card">
                  <div className="insights-stat-label">Untested Components</div>
                  <div className="insights-stat-value insights-stat-value-danger" style={{ fontSize: '2.5rem' }}>
                    {untestedComponents.length}
                  </div>
                </div>
                <div className="card insights-card">
                  <div className="insights-stat-label">Components Needing Test Cases</div>
                  <div className="insights-stat-value insights-stat-value-danger" style={{ fontSize: '2.5rem' }}>
                    {componentsNeedingTestCases.length}
                  </div>
                </div>
                <div className="card insights-card">
                  <div className="insights-stat-label">Total Test Plans</div>
                  <div className="insights-stat-value" style={{ fontSize: '2.5rem' }}>
                    {testPlans.length}
                  </div>
                </div>
                <div className="card insights-card">
                  <div className="insights-stat-label">Active Testers</div>
                  <div className="insights-stat-value" style={{ fontSize: '2.5rem' }}>
                    {testerPerformance.length}
                  </div>
                </div>
                <div className="card insights-card">
                  <div className="insights-stat-label">Unique Bugs</div>
                  <div className="insights-stat-value insights-stat-value-danger" style={{ fontSize: '2.5rem' }}>
                    {(() => {
                      const filteredSessions = sessions.filter(s => {
                        if (selectedMilestone !== 'all-time' && s.milestone !== selectedMilestone) return false;
                        return s.status === 'completed';
                      });
                      // Collect all bug URLs - completely unique (no dupes across sessions or components)
                      const uniqueBugUrls = new Set<string>();
                      filteredSessions.forEach(s => {
                        if (s.bugUrls && s.bugUrls.length > 0) {
                          s.bugUrls.forEach(bu => {
                            if (typeof bu === 'string') return;
                            if (bu.url && bu.url.trim()) {
                              uniqueBugUrls.add(bu.url.trim());
                            }
                          });
                        }
                      });
                      return uniqueBugUrls.size;
                    })()}
                  </div>
                </div>
                <div className="card insights-card">
                  <div className="insights-stat-label">Bugged Components</div>
                  <div className="insights-stat-value insights-stat-value-danger" style={{ fontSize: '2.5rem' }}>
                    {(() => {
                      const filteredSessions = sessions.filter(s => {
                        if (selectedMilestone !== 'all-time' && s.milestone !== selectedMilestone) return false;
                        return s.status === 'completed';
                      });
                      // Count unique (bugUrl + componentId) combinations
                      // Same URL on same component across different platforms = 1
                      // Same URL on different components = counts for each component
                      const uniqueBugComponentPairs = new Set<string>();
                      filteredSessions.forEach(s => {
                        if (s.bugUrls && s.bugUrls.length > 0) {
                          s.bugUrls.forEach(bu => {
                            if (typeof bu === 'string') return;
                            const url = bu.url?.trim();
                            const componentId = bu.componentId || bu.subDepId;
                            if (url && componentId) {
                              // Create unique key for URL + component combination
                              uniqueBugComponentPairs.add(`${url}::${componentId}`);
                            }
                          });
                        }
                      });
                      return uniqueBugComponentPairs.size;
                    })()}
                  </div>
                </div>
                <div className="card insights-card">
                  <div className="insights-stat-label">Unique PR Feedback</div>
                  <div className="insights-stat-value insights-stat-value-primary" style={{ fontSize: '2.5rem' }}>
                    {(() => {
                      const filteredSessions = sessions.filter(s => {
                        if (selectedMilestone !== 'all-time' && s.milestone !== selectedMilestone) return false;
                        return s.status === 'completed';
                      });
                      // Collect all PR feedback comment URLs - completely unique (no dupes across sessions or components)
                      const uniquePRFeedbackUrls = new Set<string>();
                      filteredSessions.forEach(s => {
                        if (s.prFeedbacks && s.prFeedbacks.length > 0) {
                          s.prFeedbacks.forEach(fb => {
                            if (fb.commentUrl && fb.commentUrl.trim()) {
                              uniquePRFeedbackUrls.add(fb.commentUrl.trim());
                            }
                          });
                        }
                      });
                      return uniquePRFeedbackUrls.size;
                    })()}
                  </div>
                </div>
                <div className="card insights-card">
                  <div className="insights-stat-label">PR Feedback per Component</div>
                  <div className="insights-stat-value insights-stat-value-primary" style={{ fontSize: '2.5rem' }}>
                    {(() => {
                      const filteredSessions = sessions.filter(s => {
                        if (selectedMilestone !== 'all-time' && s.milestone !== selectedMilestone) return false;
                        return s.status === 'completed';
                      });
                      // Count unique (commentUrl + componentId) combinations
                      // Same commentUrl on same component across different sessions = 1
                      // Same commentUrl on different components = counts for each component
                      const uniquePRFeedbackComponentPairs = new Set<string>();
                      filteredSessions.forEach(s => {
                        if (s.prFeedbacks && s.prFeedbacks.length > 0) {
                          s.prFeedbacks.forEach(fb => {
                            const commentUrl = fb.commentUrl?.trim();
                            const componentId = fb.componentId;
                            if (commentUrl && componentId) {
                              // Create unique key for commentUrl + component combination
                              uniquePRFeedbackComponentPairs.add(`${commentUrl}::${componentId}`);
                            }
                          });
                        }
                      });
                      return uniquePRFeedbackComponentPairs.size;
                    })()}
                  </div>
                </div>
              </div>

              <div className="insights-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))' }}>
                <div className="card insights-card">
                  <div className="flex justify-between items-center mb-md">
                    <h3 className="insights-card-title" style={{ margin: 0 }}>Milestone Progression</h3>
                    <select
                      value={overviewMilestoneFilter}
                      onChange={(e) => setOverviewMilestoneFilter(e.target.value)}
                      style={{ fontSize: '0.875rem', padding: '0.5rem', width: '100px', minWidth: '100px', maxWidth: '100px' }}
                    >
                      <option value="all-time">All Time</option>
                      {getAvailableMilestones().map(milestone => {
                        const milestoneMeta = milestones[milestone];
                        const dateRange = milestoneMeta 
                          ? (milestoneMeta.endDate 
                              ? `${formatDate(milestoneMeta.startDate, 'MMM dd, yyyy')} - ${formatDate(milestoneMeta.endDate, 'MMM dd, yyyy')}`
                              : `Started: ${formatDate(milestoneMeta.startDate, 'MMM dd, yyyy')}`)
                          : '';
                        return (
                          <option key={milestone} value={milestone}>
                            {milestone}{dateRange ? ` (${dateRange})` : ''}
                          </option>
                        );
                      })}
                    </select>
                          </div>
                  <div style={{ marginTop: '1rem' }}>
                    {(() => {
                      if (overviewMilestoneFilter === 'all-time') {
                        return (
                          <div className="text-center p-md hint">
                            Select a milestone to view completion status
                          </div>
                        );
                      }

                      const milestoneCompletion = getMilestoneCompletion().find(mc => mc.milestone === overviewMilestoneFilter);
                      
                      if (!milestoneCompletion) {
                        return (
                          <div className="text-center p-md hint">
                            No data available for milestone {overviewMilestoneFilter}
                        </div>
                        );
                      }

                      const milestoneMeta = milestones[milestoneCompletion.milestone];
                      const dateRange = milestoneMeta 
                        ? (milestoneMeta.endDate 
                            ? `${formatDate(milestoneMeta.startDate, 'MMM dd, yyyy')} - ${formatDate(milestoneMeta.endDate, 'MMM dd, yyyy')}`
                            : `Started: ${formatDate(milestoneMeta.startDate, 'MMM dd, yyyy')}`)
                        : null;
                      
                      return (
                        <div>
                          <div className="flex justify-between items-center mb-md">
                            <div>
                              <span style={{ fontSize: '1.1rem', fontWeight: '600' }}>{milestoneCompletion.milestone}</span>
                              {dateRange && (
                                <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginTop: '0.25rem' }}>
                                  {dateRange}
                                </div>
                              )}
                            </div>
                            <div className={`insights-percentage ${milestoneCompletion.overallCompletion >= 80 ? 'insights-percentage-success' : milestoneCompletion.overallCompletion >= 50 ? 'insights-percentage-warning' : 'insights-percentage-danger'}`} style={{ fontSize: '1.5rem' }}>
                              {milestoneCompletion.overallCompletion.toFixed(1)}%
                            </div>
                          </div>
                          <div className="insights-stats-grid" style={{ marginTop: '1rem' }}>
                            <div>
                              <div className="insights-stat-label">Test Plans Completion</div>
                              <div className={`insights-stat-value ${milestoneCompletion.testPlanCompletion >= 80 ? 'insights-stat-value-success' : milestoneCompletion.testPlanCompletion >= 50 ? 'insights-stat-value-warning' : 'insights-stat-value-danger'}`}>
                                {milestoneCompletion.testPlanCompletion.toFixed(1)}%
                              </div>
                            </div>
                            <div>
                              <div className="insights-stat-label">Sessions Completion</div>
                              <div className={`insights-stat-value ${milestoneCompletion.sessionCompletion >= 80 ? 'insights-stat-value-success' : milestoneCompletion.sessionCompletion >= 50 ? 'insights-stat-value-warning' : 'insights-stat-value-danger'}`}>
                                {milestoneCompletion.sessionCompletion.toFixed(1)}%
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                </div>
                <div className="card insights-card">
                  <h3 className="insights-card-title">Platform Coverage Summary</h3>
                  <div style={{ marginTop: '1rem' }}>
                    {platformStats.slice(0, 5).map(stat => (
                      <div key={stat.platform} style={{ marginBottom: '1rem' }}>
                        <div className="flex justify-between items-center mb-xs">
                          <span style={{ fontSize: '0.875rem', fontWeight: '600' }}>{stat.platform}</span>
                          <span className={`insights-percentage ${stat.percentage >= 80 ? 'insights-percentage-success' : stat.percentage >= 50 ? 'insights-percentage-warning' : 'insights-percentage-danger'}`} style={{ fontSize: '1rem' }}>
                            {stat.percentage.toFixed(1)}%
                          </span>
                        </div>
                        <div className="progress-track insights-progress-track">
                          <div 
                            className={`progress-fill-${stat.percentage >= 80 ? 'success' : stat.percentage >= 50 ? 'warning' : 'danger'} insights-progress-fill`}
                            style={{ width: `${Math.min(stat.percentage, 100)}%` }}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeCategory === 'coverage' && (
      <div className="tabs insights-tabs">
        <button
          className={`tab ${activeTab === 'platform-coverage' ? 'active' : ''}`}
          onClick={() => setActiveTab('platform-coverage')}
        >
          Platform Coverage
        </button>
        <button
          className={`tab ${activeTab === 'feature-coverage' ? 'active' : ''}`}
          onClick={() => setActiveTab('feature-coverage')}
        >
          Feature Coverage
        </button>
        <button
          className={`tab ${activeTab === 'untested' ? 'active' : ''}`}
          onClick={() => setActiveTab('untested')}
        >
          Untested Components ({untestedComponents.length})
        </button>
              <button
                className={`tab ${activeTab === 'needs-test-case' ? 'active' : ''}`}
                onClick={() => setActiveTab('needs-test-case')}
              >
                Needs Test Case ({componentsNeedingTestCases.length})
              </button>
            </div>
          )}

          {activeCategory === 'quality' && (
            <div className="tabs insights-tabs">
        <button
          className={`tab ${activeTab === 'bug-rates' ? 'active' : ''}`}
          onClick={() => setActiveTab('bug-rates')}
        >
          Bug Rates
        </button>
              <button
                className={`tab ${activeTab === 'component-health' ? 'active' : ''}`}
                onClick={() => setActiveTab('component-health')}
              >
                Component Health
              </button>
        <button
          className={`tab ${activeTab === 'bug-analysis' ? 'active' : ''}`}
          onClick={() => setActiveTab('bug-analysis')}
        >
          Bug Analysis
        </button>
        <button
          className={`tab ${activeTab === 'bug-discovery-rate' ? 'active' : ''}`}
          onClick={() => setActiveTab('bug-discovery-rate')}
        >
          Bug Discovery Rate
        </button>
        <button
          className={`tab ${activeTab === 'bug-tracker' ? 'active' : ''}`}
          onClick={() => setActiveTab('bug-tracker')}
        >
          Bug Tracker
        </button>
        <button
          className={`tab ${activeTab === 'trends' ? 'active' : ''}`}
          onClick={() => setActiveTab('trends')}
        >
          Testing Trends
        </button>
        <button
          className={`tab ${activeTab === 'pr-feedback-value' ? 'active' : ''}`}
          onClick={() => setActiveTab('pr-feedback-value')}
        >
          PR Feedback Value
        </button>
            </div>
          )}

          {activeCategory === 'planning' && (
            <div className="tabs insights-tabs">
        <button
                className={`tab ${activeTab === 'test-plan-completion' ? 'active' : ''}`}
                onClick={() => setActiveTab('test-plan-completion')}
        >
                Test Plan Completion
        </button>
        <button
          className={`tab ${activeTab === 'milestone-completion' ? 'active' : ''}`}
          onClick={() => setActiveTab('milestone-completion')}
        >
          Milestone Completion
        </button>
        <button
          className={`tab ${activeTab === 'platform-map' ? 'active' : ''}`}
          onClick={() => setActiveTab('platform-map')}
        >
          Platform Testing Map
        </button>
            </div>
          )}

          {activeCategory === 'performance' && (userRole === 'admin' || userRole === 'manager') && (
            <div className="tabs insights-tabs">
        <button
                className={`tab ${activeTab === 'tester-performance' ? 'active' : ''}`}
                onClick={() => setActiveTab('tester-performance')}
        >
                Tester Performance
        </button>
      </div>
          )}

      {activeCategory === 'coverage' && activeTab === 'platform-coverage' && (
        <div>
          <h2 className="insights-section-title">Platform Testing Coverage</h2>
          <p className="insights-section-subtitle">
            Percentage of components tested per platform{selectedMilestone === 'all-time' ? ' (all time)' : ` for milestone ${selectedMilestone}`}.
            Shows which components have been tested on each platform.
          </p>
          <div className="insights-grid">
            {platformStats.map(stat => (
              <div key={stat.platform} className="card insights-card">
                <div className="insights-card-header">
                  <h3 className="insights-card-title">{stat.platform}</h3>
                  <div className={`insights-percentage ${stat.percentage >= 80 ? 'insights-percentage-success' : stat.percentage >= 50 ? 'insights-percentage-warning' : 'insights-percentage-danger'}`}>
                    {stat.percentage.toFixed(1)}%
                  </div>
                </div>
                <div style={{ marginBottom: '1rem' }}>
                  <div className="progress-track insights-progress-track">
                    <div 
                      className={`progress-fill-${stat.percentage >= 80 ? 'success' : stat.percentage >= 50 ? 'warning' : 'danger'} insights-progress-fill`}
                      style={{ width: `${Math.min(stat.percentage, 100)}%` }}
                    />
                  </div>
                </div>
                <div className="insights-stats-row">
                  <div>
                    <strong>{stat.testedComponents}</strong> of <strong>{stat.totalComponents}</strong> components tested
                  </div>
                  <div>
                    <strong>{stat.sessions.length}</strong> session{stat.sessions.length !== 1 ? 's' : ''} completed
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {activeCategory === 'coverage' && activeTab === 'feature-coverage' && (
        <div>
          <h2 className="insights-section-title">Feature Coverage</h2>
          <p className="insights-section-subtitle">
            Percentage of components tested per feature{selectedMilestone === 'all-time' ? ' (all time)' : ` for milestone ${selectedMilestone}`}.
          </p>
          <div className="insights-grid">
            {featureCoverage
              .sort((a, b) => a.coverage - b.coverage)
              .map(fc => {
                // Calculate platform coverage details
                // Filter components by milestone first, then by feature
                const milestoneFilteredComponents = filterComponentsByMilestone(components, selectedMilestone);
                const featureComponents = milestoneFilteredComponents.filter(c => c.featureId === fc.feature.id);
                const featurePlatforms = new Set<Platform>();
                featureComponents.forEach(component => {
                  if (component.platforms && component.platforms.length > 0) {
                    component.platforms.forEach((platform: Platform) => {
                      featurePlatforms.add(platform);
                    });
                  }
                });

                // Count tested platforms
                // Filter sessions by milestone first, then filter by completed status
                const milestoneFilteredSessions = filterSessionsByMilestone(sessions, selectedMilestone);
                const testedPlatforms = new Set<Platform>();
                milestoneFilteredSessions.filter(s => {
                  if (s.status !== 'completed') return false;
                  return true;
                }).forEach(session => {
                  if (session.componentStatuses && session.componentStatuses.length > 0) {
                    session.componentStatuses.forEach(cs => {
                      if (cs.tested && cs.componentId && cs.testedPlatforms) {
                        const component = featureComponents.find(c => c.id === cs.componentId);
                        if (component && component.platforms) {
                          cs.testedPlatforms.forEach((platform: Platform) => {
                            if (component.platforms?.includes(platform)) {
                              testedPlatforms.add(platform);
                            }
                          });
                        }
                      }
                    });
                  } else if (session.subDepStatuses && session.subDepStatuses.length > 0) {
                    session.subDepStatuses.forEach(sds => {
                      if (sds.tested && sds.subDepId && sds.testedPlatforms) {
                        const component = featureComponents.find(c => c.id === sds.subDepId);
                        if (component && component.platforms) {
                          sds.testedPlatforms.forEach((platform: Platform) => {
                            if (component.platforms?.includes(platform)) {
                              testedPlatforms.add(platform);
                            }
                          });
                        }
                      }
                    });
                  }
                });

                const totalPlatforms = featurePlatforms.size;
                const testedPlatformCount = testedPlatforms.size;

                return (
                <div key={fc.feature.id} className="card insights-card">
                  <div className="insights-card-header">
                    <h3 className="insights-card-title">{fc.feature.name}</h3>
                    <div className={`insights-percentage ${fc.coverage >= 80 ? 'insights-percentage-success' : fc.coverage >= 50 ? 'insights-percentage-warning' : 'insights-percentage-danger'}`}>
                      {fc.coverage.toFixed(1)}%
                    </div>
                  </div>
                  <div className="insights-stats-row">
                    <div>
                      <strong>{fc.testedComponents}</strong> of <strong>{fc.totalComponents}</strong> components tested
                    </div>
                  </div>
                    {totalPlatforms > 0 && (
                      <div className="insights-stats-row" style={{ marginTop: '0.5rem' }}>
                        <div>
                          <strong>{testedPlatformCount}</strong> of <strong>{totalPlatforms}</strong> platform{totalPlatforms !== 1 ? 's' : ''} tested
                          {totalPlatforms > 0 && (
                            <span className="hint" style={{ marginLeft: '0.5rem' }}>
                              ({testedPlatformCount === 0 ? '0%' : `${((testedPlatformCount / totalPlatforms) * 100).toFixed(0)}%`} platform coverage)
                            </span>
                          )}
                </div>
                      </div>
                    )}
                  </div>
                );
              })}
          </div>
        </div>
      )}

      {activeCategory === 'coverage' && activeTab === 'untested' && (
        <div>
          <h2 className="insights-section-title">Untested Components</h2>
          <p className="insights-section-subtitle">
            Components that have not been tested{selectedMilestone === 'all-time' ? ' (all time)' : ` for milestone ${selectedMilestone}`}, grouped by platform.
            These can be used to create new test sessions or Qase test runs.
          </p>
          {untestedComponents.length === 0 ? (
            <div className="card insights-empty-state">
              <div className="insights-empty-icon">✅</div>
              <h3 className="insights-empty-title">All components have been tested!</h3>
              <p className="insights-empty-text">Great job keeping up with testing coverage.</p>
            </div>
          ) : (
            <div className="flex flex-col gap-lg">
              {platforms.map(p => {
                const platform = p.name as Platform;
                const platformUntested = untestedComponents.filter(ut => ut.platform === platform);
                if (platformUntested.length === 0) return null;
                
                return (
                  <div key={platform} className="card insights-card">
                    <h3 className="insights-platform-header">
                      {platform} ({platformUntested.length} untested)
                    </h3>
                    <div className="grid gap-sm">
                      {platformUntested.map((ut, idx) => (
                        <div
                          key={`${ut.component.id}-${ut.platform}`}
                          className="card flex justify-between items-center p-md"
                        >
                          <div className="flex-1">
                            <div className="value mb-sm">{ut.component.name}</div>
                            <div className="subtitle">
                              Feature: <strong>{ut.feature.name}</strong>
                            </div>
                          </div>
                          {ut.lastTested && (
                            <div className="hint" style={{ marginLeft: '1rem' }}>
                              Last tested: {formatDate(ut.lastTested, 'MMM dd, yyyy')}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {activeCategory === 'quality' && activeTab === 'bug-rates' && (
        <div>
          <h2 className="insights-section-title">Bug Rates & Statistics</h2>
          <p className="insights-section-subtitle">
            Bug rates and average session duration{selectedMilestone === 'all-time' ? ' (all time)' : ` for milestone ${selectedMilestone}`}.
          </p>
          
          {/* Sub-tabs for Bug Rates */}
          <div className="tabs insights-subtab-tabs">
            <button
              className={`tab ${bugRatesSubTab === 'platform' ? 'active' : ''}`}
              onClick={() => setBugRatesSubTab('platform')}
            >
              Per Platform
            </button>
            <button
              className={`tab ${bugRatesSubTab === 'session-type' ? 'active' : ''}`}
              onClick={() => setBugRatesSubTab('session-type')}
            >
              Per Session Type
            </button>
          </div>

          {bugRatesSubTab === 'platform' && (
            <div>
              <h3 className="insights-subtab-title">
                Bug Rates by Platform
              </h3>
              <div className="insights-grid">
                {bugRatesPerPlatform.map(stat => (
                  <div key={stat.platform} className="card insights-card">
                    <div className="insights-card-header">
                      <h3 className="insights-card-title">{stat.platform}</h3>
                      <div className={`insights-bug-rate ${stat.bugRate >= 30 ? 'insights-percentage-danger' : stat.bugRate >= 15 ? 'insights-percentage-warning' : 'insights-percentage-success'}`}>
                        {stat.bugRate.toFixed(1)}% bug rate
                      </div>
                    </div>
                    <div className="insights-stats-grid">
                      <div>
                        <div className="insights-stat-label">Sessions</div>
                        <div className="insights-stat-value">{stat.totalSessions}</div>
                      </div>
                      <div>
                        <div className="insights-stat-label">Bugs Found</div>
                        <div className="insights-stat-value insights-stat-value-danger">{stat.totalBugs || 0}</div>
                      </div>
                      <div>
                        <div className="insights-stat-label">Cross-Platform</div>
                        <div className="insights-stat-value" style={{ color: stat.sharedBugs > 0 ? 'var(--warning)' : 'var(--text-secondary)' }}>{stat.sharedBugs || 0}</div>
                      </div>
                      <div>
                        <div className="insights-stat-label">Avg Duration</div>
                        <div className="insights-stat-value">{stat.avgDuration}m</div>
                      </div>
                      <div>
                        <div className="insights-stat-label">Avg Duration</div>
                        <div className="insights-stat-value">{stat.avgDuration}m</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {bugRatesSubTab === 'session-type' && (
            <div>
              <h3 style={{ marginBottom: '1rem', color: 'var(--text-primary)', fontSize: '1.1rem' }}>
                Bug Rates by Session Type
              </h3>
              {bugRatesPerSessionType.length === 0 ? (
                <div className="card insights-empty-state">
                  <div className="insights-empty-icon">📊</div>
                  <h3 className="insights-empty-title">No sessions found</h3>
                  <p className="insights-empty-text" style={{ marginBottom: '1rem' }}>
                    {sessions.length === 0 
                      ? 'No sessions exist in the database. Create and complete some sessions to see bug rate statistics.'
                      : `No completed sessions found for any session types${selectedMilestone === 'all-time' ? ' (all time)' : ` for milestone ${selectedMilestone}`}. Try changing the milestone filter.`}
                  </p>
                  {sessions.length > 0 && (
                    <div className="insights-info-box">
                      <p className="insights-info-text">
                        <strong>Total sessions in database:</strong> {sessions.length}<br />
                        <strong>Completed sessions:</strong> {sessions.filter(s => s.status === 'completed').length}
                      </p>
                    </div>
                  )}
                </div>
              ) : (
                <div className="insights-grid">
                  {bugRatesPerSessionType.map(stat => (
                    <div key={stat.sessionType} className="card insights-card">
                      <div className="insights-card-header">
                        <h3 className="insights-card-title">{stat.sessionType}</h3>
                        <div className={`insights-bug-rate ${stat.bugRate >= 30 ? 'insights-percentage-danger' : stat.bugRate >= 15 ? 'insights-percentage-warning' : 'insights-percentage-success'}`}>
                          {stat.bugRate.toFixed(1)}% bug rate
                        </div>
                      </div>
                      <div className="insights-stats-grid">
                        <div>
                          <div className="insights-stat-label">Sessions</div>
                          <div className="insights-stat-value">{stat.totalSessions}</div>
                        </div>
                        <div>
                          <div className="insights-stat-label">Bugs Found</div>
                          <div className="insights-stat-value insights-stat-value-danger">{stat.totalBugs}</div>
                        </div>
                        <div>
                          <div className="insights-stat-label">Avg Duration</div>
                          <div className="insights-stat-value">{stat.avgDuration}m</div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
                </div>
              )}
            </div>
          )}

      {activeCategory === 'quality' && activeTab === 'bug-analysis' && (
            <div>
          <h2 className="insights-section-title">Bug Analysis</h2>
          <p className="insights-section-subtitle">
            Most bug-prone components and features{selectedMilestone === 'all-time' ? ' (all time)' : ` for milestone ${selectedMilestone}`}.
          </p>
          
          <div className="insights-grid" style={{ marginBottom: '2rem' }}>
            {/* Most Bug-Prone Components Widget */}
            <div className="card insights-card">
              <h3 className="insights-card-title">Most Bug-Prone Components</h3>
              <p className="subtitle" style={{ fontSize: '0.75rem', marginBottom: '0.5rem' }}>
                Components with &gt;20% bug rate (bugs found in more than 1 in 5 tests)
              </p>
              <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                {mostBugProneComponents.map((item, idx) => {
                  if (!item || !item.component || !item.feature) return null;
                  return (
                  <div key={item.component.id} className="flex justify-between items-center p-sm" style={{ borderBottom: '1px solid var(--grid-line)' }}>
                    <div>
                      <div className="value" style={{ fontSize: '0.875rem' }}>{item.component.name}</div>
                      <div className="subtitle" style={{ fontSize: '0.75rem' }}>
                        {item.feature.name} | {item.totalTests} test{item.totalTests !== 1 ? 's' : ''} | {item.bugsFound} bug{item.bugsFound !== 1 ? 's' : ''}
                    </div>
                    </div>
                    <div className={`insights-percentage ${item.bugRate >= 70 ? 'insights-percentage-danger' : 'insights-percentage-warning'}`}>
                      {item.bugRate.toFixed(1)}%
                    </div>
                  </div>
                  );
                })}
                {mostBugProneComponents.length === 0 && (
                  <div className="text-center p-lg hint">No components with &gt;20% bug rate</div>
                  )}
                </div>
                        </div>

            {/* Most Bug-Prone Features Widget */}
            <div className="card insights-card">
              <h3 className="insights-card-title">Most Bug-Prone Features</h3>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '1rem', padding: '0.5rem', borderBottom: '1px solid var(--grid-line)', fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                <span style={{ width: '80px', textAlign: 'center' }}>Unique Bugs</span>
                <span style={{ width: '80px', textAlign: 'center' }}>Bugged Components</span>
              </div>
              <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                {mostBugProneFeatures.map((item, idx) => {
                  if (!item || !item.feature) return null;
                  return (
                  <div key={item.feature.id} className="flex justify-between items-center p-sm" style={{ borderBottom: '1px solid var(--grid-line)' }}>
                    <span>{item.feature.name}</span>
                    <div style={{ display: 'flex', gap: '1rem' }}>
                      <div className="insights-stat-value insights-stat-value-danger" style={{ width: '80px', textAlign: 'center' }}>{item.uniqueBugUrls}</div>
                      <div className="insights-stat-value insights-stat-value-danger" style={{ width: '80px', textAlign: 'center' }}>{item.buggedComponents}</div>
                    </div>
                      </div>
                  );
                })}
                {mostBugProneFeatures.length === 0 && (
                  <div className="text-center p-lg hint">No bug data available</div>
                )}
                        </div>
                        </div>
                        </div>
        </div>
      )}

      {activeCategory === 'quality' && activeTab === 'bug-discovery-rate' && (
                        <div>
          <h2 className="insights-section-title">Bug Discovery Rate Over Time</h2>
          <p className="insights-section-subtitle">
            Number of bugs discovered per day{selectedMilestone === 'all-time' ? ' (all time)' : ` for milestone ${selectedMilestone}`}.
          </p>
          
          <div className="card" style={{ marginBottom: '1rem' }}>
            <div className="p-lg">
              {bugDiscoveryRate.length === 0 ? (
                <div className="text-center p-lg hint">No bug discovery data available</div>
              ) : (() => {
                // Calculate summary statistics
                const totalBugUrls = bugDiscoveryRate.reduce((sum, bdr) => sum + (bdr.bugs ?? 0), 0);
                const totalBuggedComponents = bugDiscoveryRate.reduce((sum, bdr) => sum + (bdr.buggedComponents ?? 0), 0);
                const avgBugsPerDay = bugDiscoveryRate.length > 0 ? (totalBugUrls / bugDiscoveryRate.length) : 0;
                const maxBugs = Math.max(...bugDiscoveryRate.map(bdr => bdr.bugs ?? 0), 0);
                const maxBugsDate = bugDiscoveryRate.find(bdr => (bdr.bugs ?? 0) === maxBugs)?.date;

                return (
                  <>
                    {/* Summary Statistics Cards */}
                    <div className="insights-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', marginBottom: '2rem' }}>
                      <div className="card insights-card">
                        <div className="insights-stat-label">Unique Bugs</div>
                        <div className="insights-stat-value insights-stat-value-danger" style={{ fontSize: '2rem' }}>
                          {totalBugUrls}
                        </div>
                      </div>
                      <div className="card insights-card">
                        <div className="insights-stat-label">Bugged Components</div>
                        <div className="insights-stat-value insights-stat-value-danger" style={{ fontSize: '2rem' }}>
                          {totalBuggedComponents}
                        </div>
                      </div>
                      <div className="card insights-card">
                        <div className="insights-stat-label">Avg Bugs/Day</div>
                        <div className="insights-stat-value" style={{ fontSize: '2rem' }}>
                          {avgBugsPerDay.toFixed(1)}
                        </div>
                      </div>
                      <div className="card insights-card">
                        <div className="insights-stat-label">Peak Day</div>
                        <div className="insights-stat-value insights-stat-value-danger" style={{ fontSize: '1.5rem' }}>
                          {maxBugs}
                        </div>
                        {maxBugsDate && (
                          <div className="subtitle" style={{ fontSize: '0.75rem', marginTop: '0.25rem' }}>
                            {formatDate(maxBugsDate, 'MMM dd, yyyy')}
                          </div>
                        )}
                      </div>
                    </div>
                  </>
                );
              })()}
            </div>
          </div>

          <div className="card">
            <div className="p-lg">
              {bugDiscoveryRate.length === 0 ? (
                <div className="text-center p-lg hint">No bug discovery data available</div>
              ) : (() => {
                // Calculate graph dimensions and scaling
                const graphWidth = 800;
                const graphHeight = 300;
                const padding = { top: 20, right: 40, bottom: 40, left: 60 };
                const chartWidth = graphWidth - padding.left - padding.right;
                const chartHeight = graphHeight - padding.top - padding.bottom;

                // Get max bugs for Y-axis scaling
                const maxBugs = Math.max(...bugDiscoveryRate.map(bdr => bdr.bugs), 1);
                const yAxisMax = Math.ceil(maxBugs * 1.1); // Add 10% padding at top

                // Calculate points for the line
                const points = bugDiscoveryRate.map((bdr, idx) => {
                  const x = padding.left + (idx / (bugDiscoveryRate.length - 1 || 1)) * chartWidth;
                  const y = padding.top + chartHeight - (bdr.bugs / yAxisMax) * chartHeight;
                  return { x, y, bugs: bdr.bugs, date: bdr.date };
                });

                // Create path for the line
                const pathData = points.map((p, idx) => 
                  `${idx === 0 ? 'M' : 'L'} ${p.x} ${p.y}`
                ).join(' ');

                // Create area path (for fill under the line)
                const areaPath = `${pathData} L ${points[points.length - 1].x} ${padding.top + chartHeight} L ${points[0].x} ${padding.top + chartHeight} Z`;

                // Y-axis labels (whole numbers only)
                const yAxisSteps = 5;
                const yAxisLabels = Array.from({ length: yAxisSteps + 1 }, (_, i) => {
                  const value = Math.round((yAxisMax / yAxisSteps) * (yAxisSteps - i));
                  const y = padding.top + (i / yAxisSteps) * chartHeight;
                  return { value, y };
                });

                return (
                  <div style={{ overflowX: 'auto' }}>
                    <svg width={graphWidth} height={graphHeight} style={{ display: 'block' }}>
                      {/* Grid lines */}
                      {yAxisLabels.map((label, idx) => (
                        <line
                          key={idx}
                          x1={padding.left}
                          y1={label.y}
                          x2={padding.left + chartWidth}
                          y2={label.y}
                          stroke="var(--grid-line)"
                          strokeWidth="1"
                          strokeDasharray="4 4"
                          opacity="0.5"
                        />
                      ))}

                      {/* Area fill under line */}
                      <path
                        d={areaPath}
                        fill="var(--danger)"
                        fillOpacity="0.1"
                      />

                      {/* Line */}
                      <path
                        d={pathData}
                        fill="none"
                        stroke="var(--danger)"
                        strokeWidth="2"
                      />

                      {/* Data points */}
                      {points.map((point, idx) => (
                        <g key={idx}>
                          <circle
                            cx={point.x}
                            cy={point.y}
                            r="4"
                            fill="var(--danger)"
                            stroke="var(--surface)"
                            strokeWidth="2"
                          />
                          <title>
                            {formatDate(point.date, 'MMM dd, yyyy')}: {point.bugs} bug{point.bugs !== 1 ? 's' : ''}
                          </title>
                        </g>
                      ))}

                      {/* Y-axis */}
                      <line
                        x1={padding.left}
                        y1={padding.top}
                        x2={padding.left}
                        y2={padding.top + chartHeight}
                        stroke="var(--grid-line)"
                        strokeWidth="2"
                      />

                      {/* X-axis */}
                      <line
                        x1={padding.left}
                        y1={padding.top + chartHeight}
                        x2={padding.left + chartWidth}
                        y2={padding.top + chartHeight}
                        stroke="var(--grid-line)"
                        strokeWidth="2"
                      />

                      {/* Y-axis labels */}
                      {yAxisLabels.map((label, idx) => (
                        <text
                          key={idx}
                          x={padding.left - 10}
                          y={label.y + 4}
                          textAnchor="end"
                          fill="var(--text-muted)"
                          fontSize="12"
                        >
                          {label.value}
                        </text>
                      ))}

                      {/* X-axis labels (show every Nth date to avoid crowding) */}
                      {bugDiscoveryRate.map((bdr, idx) => {
                        const showLabel = bugDiscoveryRate.length <= 10 || 
                          idx === 0 || 
                          idx === bugDiscoveryRate.length - 1 || 
                          idx % Math.ceil(bugDiscoveryRate.length / 5) === 0;
                        
                        if (!showLabel) return null;
                        
                        const x = padding.left + (idx / (bugDiscoveryRate.length - 1 || 1)) * chartWidth;
                        return (
                          <text
                            key={idx}
                            x={x}
                            y={padding.top + chartHeight + 20}
                            textAnchor="middle"
                            fill="var(--text-muted)"
                            fontSize="11"
                          >
                            {formatDate(bdr.date, 'MMM dd')}
                          </text>
                        );
                      })}
                    </svg>
                      </div>
                );
              })()}
                    </div>
                </div>
        </div>
      )}

      {activeCategory === 'quality' && activeTab === 'bug-tracker' && (
        <div>
          <h2 className="insights-section-title">Bug Tracker</h2>
          <p className="insights-section-subtitle">
            All bugs organized by feature and component with clickable URLs{selectedMilestone === 'all-time' ? ' (all time)' : ` for milestone ${selectedMilestone}`}.
          </p>
          
          {(() => {
            const bugTrackerData = getBugTrackerData();
            
            if (bugTrackerData.length === 0) {
              return (
                <div className="card">
                  <div className="p-lg text-center hint">No bugs found for the selected milestone.</div>
                </div>
              );
            }

            // Calculate totals
            const totalUniqueBugUrls = bugTrackerData.reduce((sum, f) => sum + f.uniqueBugUrls, 0);
            const totalBuggedComponents = bugTrackerData.reduce((sum, f) => sum + f.buggedComponentsCount, 0);

            return (
              <>
                {/* Summary Cards */}
                <div className="insights-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', marginBottom: '2rem' }}>
                  <div className="card insights-card">
                    <div className="insights-stat-label">Features with Bugs</div>
                    <div className="insights-stat-value insights-stat-value-danger" style={{ fontSize: '2rem' }}>
                      {bugTrackerData.length}
                    </div>
                  </div>
                  <div className="card insights-card">
                    <div className="insights-stat-label">Unique Bugs</div>
                    <div className="insights-stat-value insights-stat-value-danger" style={{ fontSize: '2rem' }}>
                      {totalUniqueBugUrls}
                    </div>
                  </div>
                  <div className="card insights-card">
                    <div className="insights-stat-label">Bugged Components</div>
                    <div className="insights-stat-value insights-stat-value-danger" style={{ fontSize: '2rem' }}>
                      {totalBuggedComponents}
                    </div>
                  </div>
                </div>

                {/* Features and Components */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                  {bugTrackerData.map(featureData => (
                    <div key={featureData.feature.id} className="card" style={{ overflow: 'hidden' }}>
                      {/* Feature Header */}
                      <div style={{ 
                        padding: '1rem', 
                        backgroundColor: 'var(--surface-secondary)', 
                        borderBottom: '1px solid var(--grid-line)',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                      }}>
                        <h3 style={{ margin: 0, fontSize: '1.1rem', color: 'var(--text-primary)' }}>
                          {featureData.feature.name}
                        </h3>
                        <div style={{ display: 'flex', gap: '1.5rem' }}>
                          <div style={{ textAlign: 'center' }}>
                            <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>Unique Bugs</div>
                            <div className="insights-stat-value insights-stat-value-danger" style={{ fontSize: '1.25rem' }}>
                              {featureData.uniqueBugUrls}
                            </div>
                          </div>
                          <div style={{ textAlign: 'center' }}>
                            <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>Bugged Components</div>
                            <div className="insights-stat-value insights-stat-value-danger" style={{ fontSize: '1.25rem' }}>
                              {featureData.buggedComponentsCount}
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      {/* Components List */}
                      <div style={{ padding: '0.5rem' }}>
                        {featureData.components.map(compData => (
                          <div 
                            key={compData.component.id} 
                            style={{ 
                              padding: '0.75rem 1rem',
                              borderBottom: '1px solid var(--grid-line)',
                            }}
                          >
                            {/* Component Header */}
                            <div style={{ 
                              display: 'flex', 
                              justifyContent: 'space-between', 
                              alignItems: 'center',
                              marginBottom: '0.5rem'
                            }}>
                              <span style={{ fontWeight: 500, color: 'var(--text-primary)' }}>
                                {compData.component.name}
                              </span>
                              <span className="badge badge-danger" style={{ fontSize: '0.75rem' }}>
                                {compData.bugs.length} bug{compData.bugs.length !== 1 ? 's' : ''}
                              </span>
                            </div>
                            
                            {/* Bug URLs with Platforms */}
                            <div style={{ 
                              display: 'flex', 
                              flexDirection: 'column', 
                              gap: '0.5rem',
                              paddingLeft: '1rem',
                              borderLeft: '2px solid var(--danger)',
                              marginLeft: '0.5rem'
                            }}>
                              {compData.bugs.map((bug, idx) => (
                                <div key={idx} style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                                    <a 
                                      href={bug.url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      style={{ 
                                        fontSize: '0.875rem',
                                        color: 'var(--primary)',
                                        textDecoration: 'none',
                                        wordBreak: 'break-all'
                                      }}
                                      onMouseEnter={(e) => e.currentTarget.style.textDecoration = 'underline'}
                                      onMouseLeave={(e) => e.currentTarget.style.textDecoration = 'none'}
                                    >
                                      {bug.url}
                                    </a>
                                    {bug.platforms.length > 0 && (
                                      <div style={{ display: 'flex', gap: '0.25rem', flexWrap: 'wrap' }}>
                                        {bug.platforms.map(platform => (
                                          <span 
                                            key={platform} 
                                            className="badge badge-cyan" 
                                            style={{ fontSize: '0.65rem', padding: '0.15rem 0.4rem' }}
                                          >
                                            {platform}
                                          </span>
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </>
            );
          })()}
        </div>
      )}

      {activeCategory === 'quality' && activeTab === 'pr-feedback-value' && (
        <PRFeedbackValueTab
          sessions={sessions}
          components={components}
          features={features}
          selectedMilestone={selectedMilestone}
          filterSessionsByMilestone={filterSessionsByMilestone}
          filterComponentsByMilestone={filterComponentsByMilestone}
        />
      )}

      {activeCategory === 'quality' && activeTab === 'trends' && (
        <div>
          <h2 className="insights-section-title">Testing Trends</h2>
          <p className="insights-section-subtitle">
            Testing activity over time showing sessions completed per platform{selectedMilestone === 'all-time' ? ' (all time)' : ` for milestone ${selectedMilestone}`}.
          </p>
          
          {(() => {
            // Use all trends (already filtered by milestone)
            const filteredTrends = trends;

            // Group by date for summary
            const trendsByDate = new Map<string, { total: number; platforms: Set<Platform>; platformCounts: Map<Platform, number> }>();
            filteredTrends.forEach(trend => {
              if (!trendsByDate.has(trend.date)) {
                trendsByDate.set(trend.date, { total: 0, platforms: new Set(), platformCounts: new Map() });
              }
              const entry = trendsByDate.get(trend.date)!;
              entry.total += trend.count;
              entry.platforms.add(trend.platform);
              entry.platformCounts.set(trend.platform, (entry.platformCounts.get(trend.platform) || 0) + trend.count);
            });

            // Calculate summary statistics
            const totalSessions = filteredTrends.reduce((sum, t) => sum + t.count, 0);
            const uniqueDates = trendsByDate.size;
            const avgSessionsPerDay = uniqueDates > 0 ? (totalSessions / uniqueDates) : 0;
            const peakDay = Array.from(trendsByDate.entries())
              .sort((a, b) => b[1].total - a[1].total)[0];
            const platformTotals = new Map<Platform, number>();
            filteredTrends.forEach(t => {
              platformTotals.set(t.platform, (platformTotals.get(t.platform) || 0) + t.count);
            });

            // Calculate trend direction (compare first half vs second half)
            const sortedDates = Array.from(trendsByDate.keys()).sort();
            let trendDirection: 'increasing' | 'decreasing' | 'stable' = 'stable';
            if (sortedDates.length >= 4) {
              const midpoint = Math.floor(sortedDates.length / 2);
              const firstHalf = sortedDates.slice(0, midpoint);
              const secondHalf = sortedDates.slice(midpoint);
              const firstHalfTotal = firstHalf.reduce((sum, date) => sum + (trendsByDate.get(date)?.total || 0), 0);
              const secondHalfTotal = secondHalf.reduce((sum, date) => sum + (trendsByDate.get(date)?.total || 0), 0);
              const firstHalfAvg = firstHalfTotal / firstHalf.length;
              const secondHalfAvg = secondHalfTotal / secondHalf.length;
              
              if (secondHalfAvg > firstHalfAvg * 1.1) {
                trendDirection = 'increasing';
              } else if (secondHalfAvg < firstHalfAvg * 0.9) {
                trendDirection = 'decreasing';
              }
            }

            return (
              <>
                {/* Summary Statistics Cards */}
                <div className="insights-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', marginBottom: '2rem' }}>
                  <div className="card insights-card">
                    <div className="insights-stat-label">Total Sessions</div>
                    <div className="insights-stat-value" style={{ fontSize: '2rem' }}>
                      {totalSessions}
                    </div>
                  </div>
                  <div className="card insights-card">
                    <div className="insights-stat-label">Days with Activity</div>
                    <div className="insights-stat-value" style={{ fontSize: '2rem' }}>
                      {uniqueDates}
                    </div>
                  </div>
                  <div className="card insights-card">
                    <div className="insights-stat-label">Avg Sessions/Day</div>
                    <div className="insights-stat-value" style={{ fontSize: '2rem' }}>
                      {avgSessionsPerDay.toFixed(1)}
                    </div>
                  </div>
                  <div className="card insights-card">
                    <div className="insights-stat-label">Peak Day</div>
                    <div className="insights-stat-value" style={{ fontSize: '1.5rem' }}>
                      {peakDay ? `${peakDay[1].total} sessions` : 'N/A'}
                    </div>
                    {peakDay && (
                      <div className="subtitle" style={{ fontSize: '0.75rem', marginTop: '0.25rem' }}>
                        {formatDate(peakDay[0], 'MMM dd, yyyy')}
                      </div>
                    )}
                  </div>
                  <div className="card insights-card">
                    <div className="insights-stat-label">Activity Trend</div>
                    <div className="insights-stat-value" style={{ fontSize: '1.5rem' }}>
                      {trendDirection === 'increasing' ? (
                        <span style={{ color: 'var(--success)' }}>↑ Increasing</span>
                      ) : trendDirection === 'decreasing' ? (
                        <span style={{ color: 'var(--warning)' }}>↓ Decreasing</span>
                      ) : (
                        <span style={{ color: 'var(--text-secondary)' }}>→ Stable</span>
                      )}
                    </div>
                  </div>
                </div>

                {/* Platform Summary */}
                <div className="card" style={{ marginBottom: '2rem' }}>
                  <h3 className="insights-card-title" style={{ marginBottom: '1rem' }}>Sessions by Platform</h3>
                  <div className="insights-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))' }}>
                    {Array.from(platformTotals.entries())
                      .sort((a, b) => b[1] - a[1])
                      .map(([platform, count]) => (
                        <div key={platform} style={{ textAlign: 'center', padding: '1rem' }}>
                          <div className="insights-stat-label">{platform}</div>
                          <div className="insights-stat-value" style={{ fontSize: '1.5rem' }}>
                            {count}
                          </div>
                          <div className="subtitle" style={{ fontSize: '0.75rem' }}>
                            {totalSessions > 0 ? `${((count / totalSessions) * 100).toFixed(1)}% of total` : '0%'}
                          </div>
                        </div>
                      ))}
                  </div>
                </div>

                {/* Visual Chart */}
                {sortedDates.length > 0 && (
                  <div className="card" style={{ marginBottom: '2rem' }}>
                    <h3 className="insights-card-title" style={{ marginBottom: '1rem' }}>Sessions Over Time</h3>
                    <div style={{ overflowX: 'auto' }}>
                      {(() => {
                        const graphWidth = Math.max(800, sortedDates.length * 40);
                        const graphHeight = 300;
                        const padding = { top: 20, right: 40, bottom: 40, left: 60 };
                        const chartWidth = graphWidth - padding.left - padding.right;
                        const chartHeight = graphHeight - padding.top - padding.bottom;

                        const maxSessions = Math.max(...Array.from(trendsByDate.values()).map(v => v.total), 1);
                        const yAxisMax = Math.ceil(maxSessions * 1.1);

                        // Calculate points for each date
                        const points = sortedDates.map((date, idx) => {
                          const data = trendsByDate.get(date)!;
                          const x = padding.left + (idx / (sortedDates.length - 1 || 1)) * chartWidth;
                          const y = padding.top + chartHeight - (data.total / yAxisMax) * chartHeight;
                          return { x, y, total: data.total, date };
                        });

                        // Create path for the line
                        const pathData = points.map((p, idx) => 
                          `${idx === 0 ? 'M' : 'L'} ${p.x} ${p.y}`
                        ).join(' ');

                        // Area path
                        const areaPath = `${pathData} L ${points[points.length - 1].x} ${padding.top + chartHeight} L ${points[0].x} ${padding.top + chartHeight} Z`;

                        // Y-axis labels
                        const yAxisSteps = 5;
                        const yAxisLabels = Array.from({ length: yAxisSteps + 1 }, (_, i) => {
                          const value = (yAxisMax / yAxisSteps) * (yAxisSteps - i);
                          const y = padding.top + (i / yAxisSteps) * chartHeight;
                          return { value: Math.round(value), y };
                        });

                        return (
                          <svg width={graphWidth} height={graphHeight} style={{ display: 'block' }}>
                            {/* Grid lines */}
                            {yAxisLabels.map((label, idx) => (
                              <line
                  key={idx}
                                x1={padding.left}
                                y1={label.y}
                                x2={padding.left + chartWidth}
                                y2={label.y}
                                stroke="var(--grid-line)"
                                strokeWidth="1"
                                strokeDasharray="4 4"
                                opacity="0.5"
                              />
                            ))}

                            {/* Area fill */}
                            <path
                              d={areaPath}
                              fill="var(--cyan-glow)"
                              fillOpacity="0.1"
                            />

                            {/* Line */}
                            <path
                              d={pathData}
                              fill="none"
                              stroke="var(--cyan-glow)"
                              strokeWidth="2"
                            />

                            {/* Data points */}
                            {points.map((point, idx) => (
                              <g key={idx}>
                                <circle
                                  cx={point.x}
                                  cy={point.y}
                                  r="4"
                                  fill="var(--cyan-glow)"
                                  stroke="var(--surface)"
                                  strokeWidth="2"
                                />
                                <title>
                                  {formatDate(point.date, 'MMM dd, yyyy')}: {point.total} session{point.total !== 1 ? 's' : ''}
                                </title>
                              </g>
                            ))}

                            {/* Y-axis */}
                            <line
                              x1={padding.left}
                              y1={padding.top}
                              x2={padding.left}
                              y2={padding.top + chartHeight}
                              stroke="var(--grid-line)"
                              strokeWidth="2"
                            />

                            {/* X-axis */}
                            <line
                              x1={padding.left}
                              y1={padding.top + chartHeight}
                              x2={padding.left + chartWidth}
                              y2={padding.top + chartHeight}
                              stroke="var(--grid-line)"
                              strokeWidth="2"
                            />

                            {/* Y-axis labels */}
                            {yAxisLabels.map((label, idx) => (
                              <text
                                key={idx}
                                x={padding.left - 10}
                                y={label.y + 4}
                                textAnchor="end"
                                fontSize="10"
                                fill="var(--text-secondary)"
                              >
                                {label.value}
                              </text>
                            ))}

                            {/* X-axis labels (show every nth date to avoid crowding) */}
                            {sortedDates.map((date, idx) => {
                              const showLabel = sortedDates.length <= 10 || idx % Math.ceil(sortedDates.length / 10) === 0 || idx === sortedDates.length - 1;
                              if (!showLabel) return null;
                              const x = padding.left + (idx / (sortedDates.length - 1 || 1)) * chartWidth;
                              return (
                                <text
                                  key={idx}
                                  x={x}
                                  y={padding.top + chartHeight + 20}
                                  textAnchor="middle"
                                  fontSize="9"
                                  fill="var(--text-secondary)"
                                  transform={`rotate(-45 ${x} ${padding.top + chartHeight + 20})`}
                                >
                                  {formatDate(date, 'MMM dd')}
                                </text>
                              );
                            })}
                          </svg>
                        );
                      })()}
                    </div>
                  </div>
                )}

                {/* Detailed List (Grouped by Date) */}
                <div className="card">
                  <h3 className="insights-card-title" style={{ marginBottom: '1rem' }}>Daily Activity Breakdown</h3>
                  <div className="flex flex-col gap-md">
                    {Array.from(trendsByDate.entries())
                      .sort((a, b) => b[0].localeCompare(a[0])) // Most recent first
                      .map(([date, data]) => (
                        <div key={date} className="card" style={{ padding: '1rem' }}>
                          <div className="flex justify-between items-center mb-sm">
                            <h4 style={{ margin: 0, fontSize: '1rem', fontWeight: '600' }}>
                              {formatDate(date, 'MMM dd, yyyy')}
                            </h4>
                            <div className="insights-stat-value" style={{ fontSize: '1.25rem' }}>
                              {data.total} session{data.total !== 1 ? 's' : ''}
                </div>
                          </div>
                          <div className="flex flex-wrap gap-xs" style={{ marginTop: '0.5rem' }}>
                            {Array.from(data.platformCounts.entries())
                              .sort((a, b) => b[1] - a[1])
                              .map(([platform, count]) => (
                                <span key={platform} className="badge badge-cyan">
                                  {platform}: {count} session{count !== 1 ? 's' : ''}
                                </span>
                              ))}
                          </div>
                        </div>
                      ))}
                    {sortedDates.length === 0 && (
                <div className="text-center p-lg hint">
                  No testing activity in the selected time period.
                </div>
              )}
            </div>
          </div>
              </>
            );
          })()}
        </div>
      )}

      {activeCategory === 'coverage' && activeTab === 'needs-test-case' && (
        <div>
          <h2 className="insights-section-title">Components Needing Test Cases</h2>
          <p className="insights-section-subtitle">
            Components that have test cases marked as needing a test case created. These should be prioritized for Qase test case creation.
          </p>
          {componentsNeedingTestCases.length === 0 ? (
            <div className="card insights-empty-state">
              <div className="insights-empty-icon">✅</div>
              <h3 className="insights-empty-title">All test cases are created!</h3>
              <p className="insights-empty-text">No components currently have test cases marked as needing test cases.</p>
            </div>
          ) : (
            <div className="flex flex-col gap-lg">
              {componentsNeedingTestCases.map((item, idx) => (
                <div key={`${item.component.id}-${idx}`} className="card insights-card">
                  <div className="flex justify-between items-start mb-md">
                    <div>
                      <h3 className="insights-platform-header mb-sm">{item.component.name}</h3>
                      <p className="subtitle">
                        Feature: <strong>{item.feature.name}</strong>
                      </p>
                    </div>
                    <div className="badge badge-warning">
                      {item.testCases.length} test case{item.testCases.length !== 1 ? 's' : ''} need{item.testCases.length === 1 ? 's' : ''} creation
                    </div>
                  </div>
                  <div className="grid gap-sm">
                    {item.testCases.map(testCase => (
                      <div
                        key={testCase.id}
                        className="card flex justify-between items-center p-md"
                      >
                        <div className="flex-1">
                          <div className="value mb-xs">{testCase.title}</div>
                          {testCase.description && (
                            <div className="subtitle text-sm" style={{ fontSize: '0.875rem' }}>
                              {testCase.description}
                            </div>
                          )}
                        </div>
                        {testCase.qaseId && (
                          <div className="hint" style={{ marginLeft: '1rem' }}>
                            Qase ID: {testCase.qaseId}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {activeCategory === 'planning' && activeTab === 'platform-map' && (() => {
        const mapData = getPlatformTestingMap();
        
        // Find max duration for color scaling
        let maxDuration = 0;
        Object.values(mapData).forEach(platformData => {
          Object.values(platformData).forEach(data => {
            if (data.totalDuration > maxDuration) {
              maxDuration = data.totalDuration;
            }
          });
        });

        // Helper to get color intensity based on duration - theme-aware
        const getColorIntensity = (duration: number): string => {
          const currentTheme = document.documentElement.getAttribute('data-theme');
          const isMidnight = currentTheme === 'midnight';
          
          if (maxDuration === 0) {
            // Light color for zero duration - theme-aware
            return isMidnight ? 'hsl(270, 40%, 20%)' : 'hsl(195, 60%, 20%)';
          }
          
          const intensity = Math.min(1, duration / maxDuration);
          
          if (isMidnight) {
            // Purple scale for Midnight theme
            const hue = 270; // Purple
            const saturation = 40 + (intensity * 50); // 40-90%
            const lightness = 20 + (intensity * 30); // 20-50%
            return `hsl(${hue}, ${saturation}%, ${lightness}%)`;
          } else {
            // Cyan/blue scale for Neon Grid theme
            const hue = 195; // Cyan-blue
            const saturation = 60 + (intensity * 40); // 60-100%
            const lightness = 20 + (intensity * 30); // 20-50%
            return `hsl(${hue}, ${saturation}%, ${lightness}%)`;
          }
        };

        // Helper to format duration
        const formatDuration = (seconds: number) => {
          const hours = Math.floor(seconds / 3600);
          const minutes = Math.floor((seconds % 3600) / 60);
          if (hours > 0) {
            return `${hours}h ${minutes}m`;
          }
          return `${minutes}m`;
        };

        return (
          <div>
            <h2 className="insights-section-title">Platform Testing Map</h2>
            <p className="insights-section-subtitle">
              Visual map showing time allocation per platform and session type{selectedMilestone === 'all-time' ? ' (all time)' : ` for milestone ${selectedMilestone}`}.
              Darker colors indicate more time spent. Use this to see where testing time was allocated during a release.
            </p>

            {Object.keys(mapData).length === 0 ? (
              <div className="card insights-empty-state">
                <div className="insights-empty-icon">📊</div>
                <h3 className="insights-empty-title">No data available</h3>
                <p className="insights-empty-text">No completed sessions found{selectedMilestone === 'all-time' ? '' : ` for milestone ${selectedMilestone}`}.</p>
              </div>
            ) : (
              <>

            {/* Heatmap Table */}
            <div className="card" style={{ overflowX: 'auto', marginBottom: '2rem' }}>
              <div style={{ minWidth: '800px' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr>
                      <th style={{ 
                        padding: '1rem', 
                        textAlign: 'left', 
                        borderBottom: '2px solid var(--grid-line)',
                        position: 'sticky',
                        left: 0,
                        backgroundColor: 'var(--surface)',
                        zIndex: 2
                      }}>
                        Platform / Session Type
                      </th>
                      {sessionTypes.map(st => (
                        <th 
                          key={st.id}
                          style={{ 
                            padding: '1rem', 
                            textAlign: 'center', 
                            borderBottom: '2px solid var(--grid-line)',
                            minWidth: '120px'
                          }}
                        >
                          {st.name}
                        </th>
                      ))}
                      <th style={{ 
                        padding: '1rem', 
                        textAlign: 'center', 
                        borderBottom: '2px solid var(--grid-line)',
                        backgroundColor: 'var(--surface-alt)'
                      }}>
                        Total Time
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {platforms.map((p, idx) => {
                      const platform = p.name;
                      const platformData = mapData[platform] || {};
                      
                      // Calculate total time for this platform
                      const totalTime = Object.values(platformData).reduce((sum, data) => sum + data.totalDuration, 0);
                      
                      return (
                        <tr key={p.id} style={{ 
                          backgroundColor: idx % 2 === 0 ? 'var(--surface)' : 'var(--surface-alt)'
                        }}>
                          <td style={{ 
                            padding: '1rem', 
                            fontWeight: '600',
                            position: 'sticky',
                            left: 0,
                            backgroundColor: idx % 2 === 0 ? 'var(--surface)' : 'var(--surface-alt)',
                            zIndex: 1
                          }}>
                            {platform}
                          </td>
                          {sessionTypes.map(st => {
                            const data = platformData[st.name] || { totalDuration: 0, sessionCount: 0, subDepCount: 0 };
                            const color = getColorIntensity(data.totalDuration);
                            
                            return (
                              <td 
                                key={st.id}
                                style={{ 
                                  padding: '1rem', 
                                  textAlign: 'center',
                                  backgroundColor: color,
                                  color: data.totalDuration > maxDuration * 0.4 ? 'white' : 'var(--text-primary)',
                                  border: '1px solid var(--grid-line)',
                                  cursor: 'pointer',
                                  position: 'relative'
                                }}
                                title={`${formatDuration(data.totalDuration)} | ${data.sessionCount} session${data.sessionCount !== 1 ? 's' : ''}`}
                              >
                                <div style={{ fontWeight: '600', marginBottom: '0.25rem' }}>
                                  {formatDuration(data.totalDuration)}
                                </div>
                                <div style={{ fontSize: '0.75rem', opacity: 0.9 }}>
                                  {data.sessionCount} session{data.sessionCount !== 1 ? 's' : ''}
                                </div>
                              </td>
                            );
                          })}
                          <td style={{ 
                            padding: '1rem', 
                            textAlign: 'center',
                            fontWeight: '600',
                            backgroundColor: 'var(--surface-alt)'
                          }}>
                            {formatDuration(totalTime)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
              </>
            )}
          </div>
        );
      })()}

      {/* Tester Performance Tab */}
      {activeCategory === 'performance' && (userRole === 'admin' || userRole === 'manager') && activeTab === 'tester-performance' && (
        <div>
          <h2 className="insights-section-title">Tester Performance & Productivity</h2>
          <p className="insights-section-subtitle">
            Performance metrics for all testers{selectedMilestone === 'all-time' ? ' (all time)' : ` for milestone ${selectedMilestone}`}.
          </p>
          {testerPerformance.length === 0 ? (
            <div className="card insights-empty-state">
              <div className="insights-empty-icon">📊</div>
              <h3 className="insights-empty-title">No tester data found</h3>
              <p className="insights-empty-text">No sessions found for the selected time period.</p>
            </div>
          ) : (
            <div className="insights-grid">
              {testerPerformance.map(tester => (
                <div key={tester.userId} className="card insights-card">
                  <div className="insights-card-header">
                    <h3 className="insights-card-title">{tester.displayName}</h3>
                    <div style={{ textAlign: 'right' }}>
                      <div className="subtitle" style={{ fontSize: '0.75rem', marginBottom: '0.25rem' }}>
                        Completion Rate
                      </div>
                      <div className={`insights-percentage ${tester.completionRate >= 80 ? 'insights-percentage-success' : tester.completionRate >= 50 ? 'insights-percentage-warning' : 'insights-percentage-danger'}`}>
                        {tester.completionRate.toFixed(1)}%
                      </div>
                    </div>
                  </div>
                  <div className="insights-stats-grid">
                    <div>
                      <div className="insights-stat-label">Sessions Completed</div>
                      <div className="insights-stat-value">{tester.sessionsCompleted}</div>
                    </div>
                    <div>
                      <div className="insights-stat-label">Sessions Created</div>
                      <div className="insights-stat-value">{tester.sessionsCreated}</div>
                    </div>
                    <div>
                      <div className="insights-stat-label">Avg Duration</div>
                      <div className="insights-stat-value">{Math.round(tester.avgDuration)}m</div>
                    </div>
                    <div>
                      <div className="insights-stat-label">Components Tested</div>
                      <div className="insights-stat-value">{tester.componentsTested}</div>
                    </div>
                    <div>
                      <div className="insights-stat-label">Bugs Found</div>
                      <div className="insights-stat-value insights-stat-value-danger">{tester.bugsFound}</div>
                    </div>
                    <div>
                      <div className="insights-stat-label">Active Sessions</div>
                      <div className="insights-stat-value">{tester.activeSessions}</div>
                    </div>
                    {tester.primaryPlatform && (
                      <div>
                        <div className="insights-stat-label">Primary Platform</div>
                        <div className="insights-stat-value">
                          <span className="badge badge-cyan">{tester.primaryPlatform}</span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Component Health Tab */}
      {activeCategory === 'quality' && activeTab === 'component-health' && (
        <div>
          <h2 className="insights-section-title">Component Health Analytics</h2>
          <p className="insights-section-subtitle">
            Health scores based on test frequency, platform coverage, and bug rate{selectedMilestone === 'all-time' ? ' (all time)' : ` for milestone ${selectedMilestone}`}.
            <br />
            <strong>Score Formula:</strong> Test Frequency (0-30pts) + Platform Coverage (0-30pts) + Bug Rate Inverse (0-40pts) = Total (0-100pts)
            <br />
            <em>Minimum Requirement: Component must be tested at least 1 time on each assigned platform before a health score is calculated. Test Frequency: Full points if tested more than 5 times. Platform Coverage: Full points if tested on all assigned platforms. Bug Rate Inverse: Full points if no bugs found, with gradual penalty based on bugs-to-tests ratio (a single bug in many tests has minimal impact). Bug rate is weighted more heavily (40pts) as it's the primary quality indicator.</em>
          </p>
          
          {/* Search and Filter Controls */}
          <div className="card p-md mb-md">
            <div className="flex gap-md items-center">
              <div className="flex-1">
                <input
                  type="text"
                  placeholder="Search components or features..."
                  value={componentHealthSearch}
                  onChange={(e) => setComponentHealthSearch(e.target.value)}
                  className="insights-number-input"
                  style={{ width: '100%' }}
                />
              </div>
            </div>
          </div>

          {componentHealth.length === 0 ? (
            <div className="card insights-empty-state">
              <div className="insights-empty-icon">📊</div>
              <h3 className="insights-empty-title">No component data found</h3>
              <p className="insights-empty-text">No components have been tested in the selected time period.</p>
            </div>
          ) : (() => {
            // Filter and sort components
            const filteredHealth = componentHealth.filter(ch => {
              if (!ch || !ch.component || !ch.feature) return false;
              if (!componentHealthSearch) return true;
              const searchLower = componentHealthSearch.toLowerCase();
              return ch.component.name.toLowerCase().includes(searchLower) ||
                     ch.feature.name.toLowerCase().includes(searchLower);
            });

            // Group by feature for better organization
            const groupedByFeature = filteredHealth.reduce((acc, ch) => {
              if (!ch || !ch.feature) return acc;
              if (!acc[ch.feature.id]) {
                acc[ch.feature.id] = {
                  feature: ch.feature,
                  components: []
                };
              }
              acc[ch.feature.id].components.push(ch);
              return acc;
            }, {} as Record<string, { feature: Feature; components: ComponentHealth[] }>);

            const featureGroups = Object.values(groupedByFeature)
              .filter(group => group && group.feature)
              .sort((a, b) => {
                if (!a.feature || !b.feature) return 0;
                return a.feature.name.localeCompare(b.feature.name);
              });

        return (
              <div className="flex flex-col gap-lg">
                {featureGroups.map(group => {
                  if (!group || !group.feature) return null;
                  // Find feature bug rate data
                  const featureBugRate = bugRatesPerFeature.find(br => br.feature.id === group.feature.id);
                  
                  return (
                  <div key={group.feature.id} className="card insights-card">
                    <div className="flex justify-between items-start mb-md">
                      <h3 className="insights-platform-header" style={{ margin: 0 }}>{group.feature.name}</h3>
                      {featureBugRate && (
                        <div className="flex gap-md items-center">
                          <div style={{ textAlign: 'right' }}>
                            <div className="subtitle" style={{ fontSize: '0.75rem', marginBottom: '0.25rem' }}>
                              Feature Bug Rate
                          </div>
                            <div className={`insights-bug-rate ${featureBugRate.bugRate >= 30 ? 'insights-percentage-danger' : featureBugRate.bugRate >= 15 ? 'insights-percentage-warning' : 'insights-percentage-success'}`} style={{ fontSize: '1rem' }}>
                              {featureBugRate.bugRate.toFixed(1)}%
                            </div>
                          </div>
                          <div style={{ textAlign: 'right', marginLeft: '1rem' }}>
                            <div className="subtitle" style={{ fontSize: '0.75rem', marginBottom: '0.25rem' }}>
                              Sessions with bugs
                        </div>
                            <div className="insights-stat-value" style={{ fontSize: '0.875rem' }}>
                              {featureBugRate.sessionsWithBugs}/{featureBugRate.totalSessions}
                        </div>
                      </div>
                          <div style={{ textAlign: 'right', marginLeft: '1rem' }}>
                            <div className="subtitle" style={{ fontSize: '0.75rem', marginBottom: '0.25rem' }}>
                              Unique Bugs
                              </div>
                            <div className="insights-stat-value insights-stat-value-danger" style={{ fontSize: '0.875rem' }}>
                              {featureBugRate.totalBugs}
                                  </div>
                              </div>
                            </div>
                      )}
                    </div>
                    <div className="card" style={{ overflowX: 'auto' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead>
                          <tr>
                            <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '2px solid var(--grid-line)' }}>Component</th>
                            <th style={{ padding: '0.75rem', textAlign: 'center', borderBottom: '2px solid var(--grid-line)' }}>Health Score</th>
                            <th style={{ padding: '0.75rem', textAlign: 'center', borderBottom: '2px solid var(--grid-line)' }}>Test Frequency</th>
                            <th style={{ padding: '0.75rem', textAlign: 'center', borderBottom: '2px solid var(--grid-line)' }}>Bug Rate</th>
                            <th style={{ padding: '0.75rem', textAlign: 'center', borderBottom: '2px solid var(--grid-line)' }}>Bug Recurrence</th>
                            <th style={{ padding: '0.75rem', textAlign: 'center', borderBottom: '2px solid var(--grid-line)' }}>Last Tested</th>
                            <th style={{ padding: '0.75rem', textAlign: 'center', borderBottom: '2px solid var(--grid-line)' }}>Bug Trend</th>
                          </tr>
                        </thead>
                        <tbody>
                          {group.components
                            .sort((a, b) => {
                              // Sort: null scores last, then by score ascending (lowest first = most problematic)
                              if (a.healthScore === null && b.healthScore === null) {
                                return a.component.name.localeCompare(b.component.name);
                              }
                              if (a.healthScore === null) return 1;
                              if (b.healthScore === null) return -1;
                              return a.healthScore - b.healthScore;
                            })
                            .map((ch, idx) => (
                              <tr key={ch.component.id} style={{ backgroundColor: idx % 2 === 0 ? 'var(--surface)' : 'var(--surface-alt)' }}>
                                <td style={{ padding: '0.75rem' }}>{ch.component.name}</td>
                                <td style={{ padding: '0.75rem', textAlign: 'center' }}>
                                  {ch.healthScore === null ? (
                                    <span className="hint">Not tested</span>
                                  ) : (
                                    <div className={`insights-percentage ${ch.healthScore >= 80 ? 'insights-percentage-success' : ch.healthScore >= 50 ? 'insights-percentage-warning' : 'insights-percentage-danger'}`}>
                                      {ch.healthScore}
                                    </div>
                                  )}
                                </td>
                                <td style={{ padding: '0.75rem', textAlign: 'center' }}>{ch.testFrequency}</td>
                                <td style={{ padding: '0.75rem', textAlign: 'center' }}>{ch.bugRate.toFixed(1)}%</td>
                                <td style={{ padding: '0.75rem', textAlign: 'center' }}>{ch.bugRecurrenceRate.toFixed(1)}%</td>
                                <td style={{ padding: '0.75rem', textAlign: 'center' }}>
                                  {ch.lastTested ? formatDate(ch.lastTested, 'MMM dd, yyyy') : 'Never'}
                                </td>
                                <td style={{ padding: '0.75rem', textAlign: 'center' }}>
                                  {ch.trend === 'increasing' ? (
                                    <span style={{ color: 'var(--danger)', fontWeight: '600' }}>Increasing</span>
                                  ) : ch.trend === 'decreasing' ? (
                                    <span style={{ color: 'var(--success)', fontWeight: '600' }}>Decreasing</span>
                                  ) : (
                                    <span style={{ color: 'var(--text-muted)' }}>Stable</span>
                                  )}
                                </td>
                              </tr>
                            ))}
                        </tbody>
                      </table>
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          })()}
              </div>
            )}

      {activeCategory === 'planning' && activeTab === 'milestone-completion' && (
          <div>
          <h2 className="insights-section-title">Milestone Completion</h2>
            <p className="insights-section-subtitle">
            Completion status for all milestones, including test plan and session completion rates.
          </p>
                <div className="insights-grid">
            {milestoneCompletion.map(mc => {
              const milestoneMeta = milestones[mc.milestone];
              const dateRange = milestoneMeta 
                ? (milestoneMeta.endDate 
                    ? `${formatDate(milestoneMeta.startDate, 'MMM dd, yyyy')} - ${formatDate(milestoneMeta.endDate, 'MMM dd, yyyy')}`
                    : `Started: ${formatDate(milestoneMeta.startDate, 'MMM dd, yyyy')}`)
                : null;
              
              return (
              <div key={mc.milestone} className="card insights-card">
                <div className="insights-card-header">
                  <div>
                    <h3 className="insights-card-title">{mc.milestone}</h3>
                    {dateRange && (
                      <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginTop: '0.25rem' }}>
                        {dateRange}
                      </div>
                    )}
                  </div>
                  <div className={`insights-percentage ${mc.overallCompletion >= 80 ? 'insights-percentage-success' : mc.overallCompletion >= 50 ? 'insights-percentage-warning' : 'insights-percentage-danger'}`}>
                    {mc.overallCompletion.toFixed(1)}%
                    </div>
                  </div>
                <div className="insights-stats-grid" style={{ marginTop: '1rem' }}>
                          <div>
                    <div className="insights-stat-label">Test Plans</div>
                    <div className="insights-stat-value">{mc.testPlanCompletion.toFixed(1)}%</div>
                    </div>
                  <div>
                    <div className="insights-stat-label">Sessions</div>
                    <div className="insights-stat-value">{mc.sessionCompletion.toFixed(1)}%</div>
                  </div>
                    </div>
                  </div>
              );
            })}
                    </div>
                  </div>
      )}

      {/* Test Plan Completion Tab */}
      {activeCategory === 'planning' && activeTab === 'test-plan-completion' && (
          <div>
          <h2 className="insights-section-title">Test Plan Completion</h2>
            <p className="insights-section-subtitle">
            Completion status for all test plans. Shows if components were tested according to test plan platforms, or tested in milestone.
            </p>
          {testPlanCompletion.length === 0 ? (
              <div className="card insights-empty-state">
                <div className="insights-empty-icon">📊</div>
              <h3 className="insights-empty-title">No test plans found</h3>
              <p className="insights-empty-text">No test plans exist for this product.</p>
                </div>
            ) : (
            <div className="flex flex-col gap-lg">
              {testPlanCompletion.map(tpc => (
                <div key={tpc.testPlan.id} className="card insights-card">
                  <div className="flex justify-between items-start mb-md">
                    <div>
                      <h3 className="insights-platform-header mb-sm">{tpc.testPlan.title}</h3>
                      <p className="subtitle">
                        Milestone: <strong>{tpc.testPlan.milestone || 'N/A'}</strong> | 
                        Completion: <strong>{tpc.completionRate.toFixed(1)}%</strong>
                      </p>
              </div>
                    <button
                      className="btn btn-secondary"
                      onClick={() => setSelectedTestPlan(tpc.testPlan)}
                    >
                      View Test Plan
                    </button>
                          </div>
                  <div className="card" style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr>
                          <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '2px solid var(--grid-line)' }}>Platform</th>
                          <th style={{ padding: '0.75rem', textAlign: 'center', borderBottom: '2px solid var(--grid-line)' }}>Status</th>
                          <th style={{ padding: '0.75rem', textAlign: 'center', borderBottom: '2px solid var(--grid-line)' }}>Components Tested</th>
                          <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '2px solid var(--grid-line)' }}>Tested Components</th>
                        </tr>
                      </thead>
                      <tbody>
                        {tpc.platformStatus.map((ps, idx) => (
                          <tr key={ps.platform} style={{ backgroundColor: idx % 2 === 0 ? 'var(--surface)' : 'var(--surface-alt)' }}>
                            <td style={{ padding: '0.75rem' }}>
                              <span className="badge badge-cyan">{ps.platform}</span>
                            </td>
                            <td style={{ padding: '0.75rem', textAlign: 'center' }}>
                              {ps.status === 'tested-per-plan' ? (
                                <span style={{ color: 'var(--success)', fontWeight: '600' }}>✅ Tested per plan</span>
                              ) : ps.status === 'tested-in-milestone' ? (
                                <span style={{ color: 'var(--warning)', fontWeight: '600' }}>⚠️ Tested in milestone</span>
                              ) : (
                                <span style={{ color: 'var(--danger)', fontWeight: '600' }}>❌ Not tested</span>
                              )}
                            </td>
                            <td style={{ padding: '0.75rem', textAlign: 'center' }}>
                              <strong>{ps.componentsTested}</strong> of <strong>{ps.componentsTotal}</strong>
                            </td>
                            <td style={{ padding: '0.75rem' }}>
                              {ps.testedComponents.length > 0 ? (
                                <div className="flex flex-wrap gap-xs">
                                  {ps.testedComponents.map(comp => (
                                    <span key={comp.id} className="badge badge-cyan" style={{ fontSize: '0.75rem' }}>
                                      {comp.name}
                                    </span>
                                  ))}
          </div>
                              ) : (
                                <span className="hint">None</span>
                              )}
                            </td>
                          </tr>
                                ))}
                      </tbody>
                    </table>
                              </div>
                            </div>
                          ))}
                      </div>
          )}
              </div>
            )}

      {/* Test Plan Details Modal */}
      {selectedTestPlan && (
        <TestPlanDetails
          scope={selectedTestPlan}
          onClose={() => setSelectedTestPlan(null)}
          features={features}
          components={components}
          onEdit={() => setSelectedTestPlan(null)}
          onDelete={() => setSelectedTestPlan(null)}
          onComplete={async () => {}}
          onReopen={async () => {}}
          zIndex={2000}
        />
      )}

      {/* PDF Report Modal */}
      <PDFReportModal
        isOpen={showReportModal}
        onClose={() => setShowReportModal(false)}
        onGenerate={(selectedSections: InsightSection[], milestone: string) => {
          // Generate PDF with the selected sections and milestone
          generateInsightsPDF({
            sessions,
            components,
            features,
            testPlans,
            users,
            platforms,
            selectedProduct,
            milestone,
            selectedSections,
            getPlatformTestingStats: () => getPlatformTestingStats(milestone),
            getUntestedComponents: () => getUntestedComponents(milestone),
            getComponentsNeedingTestCases: () => getComponentsNeedingTestCases(milestone),
            getBugRatesPerPlatform: () => getBugRatesPerPlatform(milestone),
            getBugRatesPerSessionType: () => getBugRatesPerSessionType(milestone),
            getBugRatesPerFeature: () => getBugRatesPerFeature(milestone),
            getComponentHealth: () => getComponentHealth(milestone),
            getMostBugProneComponents: () => getMostBugProneComponents(milestone),
            getMostBugProneFeatures: () => getMostBugProneFeatures(milestone),
            getBugDiscoveryRate: () => getBugDiscoveryRate(milestone),
            getBugTrackerData: () => getBugTrackerData(milestone),
            getTestingTrends: () => getTestingTrends(milestone),
            getTesterPerformance: () => {
              const result = getTesterPerformance(milestone);
              return result.map(tp => ({
                testerName: tp.displayName,
                sessionsCompleted: tp.sessionsCompleted,
                sessionsCreated: tp.sessionsCreated,
                completionRate: tp.completionRate,
                avgDuration: tp.avgDuration,
                bugsFound: tp.bugsFound,
                componentsTested: tp.componentsTested,
                activeSessions: tp.activeSessions,
                primaryPlatform: tp.primaryPlatform || undefined,
              }));
            },
            getMilestoneCompletion: () => {
              const result = getMilestoneCompletion();
              // Return all milestone completion data including test plan and session completion
              return result.map(mc => {
                const milestoneSessions = sessions.filter(s => s.milestone === mc.milestone);
                const totalSessions = milestoneSessions.length;
                const completedSessions = milestoneSessions.filter(s => s.status === 'completed').length;
                return {
                  milestone: mc.milestone,
                  testPlanCompletion: mc.testPlanCompletion || 0,
                  sessionCompletion: mc.sessionCompletion || 0,
                  overallCompletion: mc.overallCompletion || 0,
                  totalSessions,
                  completedSessions,
                };
              });
            },
            getPlatformMap: () => getPlatformTestingMap(milestone),
            getTestPlanCompletion: () => getTestPlanCompletion(milestone),
            getFeatureCoverage: () => {
              const result = getFeatureCoverage(milestone);
              return result.map(fc => ({
                feature: fc.feature,
                totalComponents: fc.totalComponents,
                testedComponents: fc.testedComponents,
                percentage: fc.coverage,
              }));
            },
          });
        }}
        availableMilestones={getAvailableMilestones()}
        userRole={userRole}
      />
                  </div>
                </div>
    </div>
  );
};

export default Insights;

